import {type ChangeEvent, useEffect, useMemo, useRef, useState} from "react";
import FavouriteButton from "./FavouriteButton";
import DraftOrderSelectorModal from "./DraftOrderSelectorModal";
import QuoteSelectorModal from "./QuoteSelectorModal";
import ProductDetailSurface from "./ProductDetailSurface";
import WarehouseSummary, {type WarehouseSummaryData} from "./WarehouseSummary";
import {createPricingCalculator, type BreakpointEntry, type BreakpointLookup, type ProductPricing, type UnitConversion, usePrice} from "../hooks/usePrice";
import type {DraftOrderItemInput, QuoteItemInput} from "../types/myaccount";
import { applyWooCartUpdates } from "../utils/applyWooCartUpdates";
import { type CurrencyDisplayMode, formatMoneyPartsWithCurrencyCode, formatMoneyWithCurrencyCode, normalizeCurrencyDisplayMode } from "../utils/formatMoneyWithCurrencyCode";
import { buildProductDetailAddToCartProps } from "../utils/buildProductDetailAddToCartProps";
import {
    onBackInStockTargetStateChange,
    resolveBackInStockTargetKeys,
    setBackInStockTargetSubscriptionState,
} from "../utils/backInStockTargets";
import { emptyProductDetailResponse, fetchCachedProductDetail, type ProductDetailResponse } from "../utils/productDetailApi";
import {toast} from "react-toastify";

interface WekaAddToCartProduct {
    id: number;
    name?: string;
    sku?: string;
}

interface SerialOption {
    serial_number: string;
    warehouse_id?: string;
    location_id?: string;
    quantity: number;
    expiration_date?: string;
    label?: string;
}

interface VariationOption {
    value: string;
    label: string;
}

interface VariationAttribute {
    key: string;
    name?: string;
    label?: string;
    options: VariationOption[];
}

interface ProductVariation {
    id: number;
    sku?: string;
    attributes: Record<string, string>;
    isInStock?: boolean;
    isPurchasable?: boolean;
    pricingUnavailableMessage?: string;
    price?: number;
    regularPrice?: number;
    comparePrice?: number;
    comparePriceSource?: string;
    basePrice?: number;
    salesPrices?: ProductPricing | null;
    breakpointLookup?: BreakpointLookup;
    baseUom?: string;
    uomEnabled?: boolean;
    availableUoms?: string[];
    unitConversions?: UnitConversion[];
    inventoryPolicy?: InventoryPolicy;
    inventoryPolicyByUom?: Record<string, InventoryPolicy>;
    serialEnabled?: boolean;
    serialForceBuyAll?: boolean;
    serialDisplayMode?: string;
    serialOptionsEndpoint?: string;
    stockStatus?: string;
    stockQuantity?: number | null;
    isOnBackorder?: boolean;
}

interface InventoryPolicy {
    selected_uom?: string;
    min_qty?: number;
    min_non_zero_qty?: number;
    step_qty?: number;
    max_qty?: number | null;
    quantity_precision?: number;
    allow_zero?: boolean;
    is_decimal_allowed?: boolean;
    step_mode_enabled?: boolean;
    can_purchase?: boolean;
    stock_fully_reserved_in_cart?: boolean;
    backorders_allowed?: boolean;
    backorders_require_notification?: boolean;
    is_on_backorder?: boolean;
    normalized_status?: string;
    status_label?: string;
}

interface QuantityClampResult {
    quantity: number;
    adjusted: boolean;
    reason: string;
}

interface CompactElementVisibility {
    productSku?: boolean | string;
    itemPrice?: boolean | string;
    calculatedPrice?: boolean | string;
    priceSummary?: boolean | string;
    quantityControls?: boolean | string;
    addToCartButton?: boolean | string;
    detailsButton?: boolean | string;
    uomSelector?: boolean | string;
    variationSelectors?: boolean | string;
    serialSelector?: boolean | string;
    messages?: boolean | string;
    backInStockButton?: boolean | string;
    draftButton?: boolean | string;
    quoteButton?: boolean | string;
    favouriteButton?: boolean | string;
}

type CompactLayout = 'controls' | 'details_modal' | 'cart_details_modal';
type CompactElementVisibilityKey = Exclude<keyof CompactElementVisibility, 'priceSummary'>;
type CompactElementVisibilityState = Record<CompactElementVisibilityKey, boolean>;

export interface WekaAddToCartProps {
    availableUoms?: string[];
    product: WekaAddToCartProduct;
    productUrl?: string;
    surfaceId?: string;
    selectedWarehouseId?: string;
    basePrice: number;
    comparePrice?: number;
    comparePriceSource?: string;
    unitConversions: UnitConversion[];
    salesPrices?: ProductPricing;
    breakpointLookup?: BreakpointLookup;
    defaultCurrencySymbol?: string;
    defaultCurrencyCode?: string;
    currencyDisplayMode?: CurrencyDisplayMode | boolean | string;
    showCurrencyCode?: boolean | string;
    taxDisplayMode?: string;
    pricesIncludeTax?: boolean | string;
    priceDisplayTaxEnabled?: boolean | string;
    priceDisplayMultiplier?: number | string;
    priceIncludingTaxMultiplier?: number | string;
    priceExcludingTaxMultiplier?: number | string;
    priceDisplaySuffix?: string;
    priceDisplaySuffixTemplate?: string;
    pricingUnavailableMessage?: string;
    quantityBreaksEnabled?: boolean;
    showQuantityBreaksTable?: boolean;
    baseUom: string;
    uomEnabled?: boolean;
    showPrice?: boolean | string;
    allowAddToCart?: boolean | string;
    showStock?: boolean | string;
    hideCalculatedPrice?: boolean;
    isPurchasable?: boolean;
    showQuoteButton?: boolean | string;
    canCreateQuotes?: boolean | string;
    canEditQuotes?: boolean | string;
    draftOrdersEnabled?: boolean | string;
    canCreateDraftOrders?: boolean | string;
    canEditDraftItems?: boolean | string;
    displayMode?: 'full' | 'compact' | string;
    compactLayout?: CompactLayout | string;
    compactDetailsButtonLabel?: string;
    inventoryPolicy?: InventoryPolicy;
    inventoryPolicyByUom?: Record<string, InventoryPolicy>;
    serialEnabled?: boolean | string;
    serialForceBuyAll?: boolean | string;
    serialDisplayMode?: string;
    serialOptionsEndpoint?: string;
    isEnquiryProduct?: boolean | string;
    enquiryButtonText?: string;
    isFavourite?: boolean | string;
    canToggleFavourites?: boolean | string;
    favouritesAjaxUrl?: string;
    favouritesNonce?: string;
    favouritesGroupsEnabled?: boolean | string;
    hideFavouriteButton?: boolean | string;
    isVariableProduct?: boolean | string;
    variationAttributes?: VariationAttribute[];
    availableVariations?: ProductVariation[];
    defaultVariationAttributes?: Record<string, string>;
    warehouseSummary?: WarehouseSummaryData;
    warehouseSummaryDisplayMode?: string;
    compactElementVisibility?: CompactElementVisibility;
    showBackorderLabelOnAddToCart?: boolean | string;
    backInStockEnabled?: boolean | string;
    backInStockButtonLabel?: string;
    backInStockSuccessMessage?: string;
    initialBackInStockTargetKeys?: string[];
}

interface BreakpointRowView extends BreakpointEntry {
    maxQty: number | null;
    label: string;
    savingsPercent: number;
}

interface CompactStartingPriceSummary {
    amount: number;
    currencyCode: string;
    currencySymbol: string;
}

const UOM_LABELS: Record<string, string> = {
    EA: 'Single Unit',
    BOX: 'Box',
    CS: 'Case',
    CTN: 'Carton',
    PKG: 'Package',
    PLT: 'Pallet',
};

const COMPARE_PRICE_SOURCE_REGULAR = 'regular_price';
const COMPARE_PRICE_SOURCE_PRODUCT_META = 'product_meta';
const COMPARE_PRICE_SOURCE_PRICING_TABLE_QTY_1 = 'pricing_table_qty_1';

const normalizePolicyPrecision = (precision: number): number => {
    if (!Number.isFinite(precision)) {
        return 0;
    }

    return Math.min(6, Math.max(0, Math.floor(precision)));
};

const formatQuantity = (value: number, decimalAllowed: boolean, precision: number): string => {
    if (!Number.isFinite(value)) {
        return '0';
    }

    if (!decimalAllowed) {
        return String(Math.max(0, Math.round(value)));
    }

    const safePrecision = normalizePolicyPrecision(precision);
    if (safePrecision <= 0) {
        return String(Math.max(0, Math.round(value)));
    }

    const multiplier = 10 ** safePrecision;
    const rounded = Math.floor((Math.max(0, value) * multiplier) + 0.000001) / multiplier;
    if (Math.abs(rounded - Math.round(rounded)) < 0.000001) {
        return String(Math.round(rounded));
    }

    return rounded.toFixed(safePrecision).replace(/\.?0+$/, '');
};

const normalizeQuantity = (value: number, decimalAllowed: boolean, precision: number): number => {
    const safe = Number.isFinite(value) ? Math.max(0, value) : 0;
    if (!decimalAllowed) {
        return Math.max(0, Math.floor(safe + 0.000001));
    }

    const safePrecision = normalizePolicyPrecision(precision);
    if (safePrecision <= 0) {
        return Math.max(0, Math.floor(safe + 0.000001));
    }

    const multiplier = 10 ** safePrecision;
    return Math.floor((safe * multiplier) + 0.000001) / multiplier;
};

const convertComparePriceForUom = (
    comparePrice: number,
    unitConversions: UnitConversion[],
    targetUom: string,
    baseUom: string
): number => {
    if (!Number.isFinite(comparePrice) || comparePrice <= 0) {
        return 0;
    }

    const normalizedTargetUom = String(targetUom || '').trim().toUpperCase();
    const normalizedBaseUom = String(baseUom || 'EA').trim().toUpperCase() || 'EA';
    if (normalizedTargetUom === '' || normalizedTargetUom === normalizedBaseUom) {
        return comparePrice;
    }

    const conversion = (unitConversions || []).find((row) =>
        String(row.from || '').trim().toUpperCase() === normalizedTargetUom
        && String(row.to || '').trim().toUpperCase() === normalizedBaseUom
    );
    if (!conversion) {
        return 0;
    }

    const factor = Number(conversion.factor || 0);
    return Number.isFinite(factor) && factor > 0 ? comparePrice * factor : 0;
};

const findFirstBreakpointComparePrice = (
    breakpointLookup: BreakpointLookup | undefined,
    targetUom: string,
    unitConversions: UnitConversion[],
    baseUom: string
): number => {
    const normalizedTargetUom = String(targetUom || '').trim().toUpperCase();
    const normalizedBaseUom = String(baseUom || 'EA').trim().toUpperCase() || 'EA';

    const resolveDirectPrice = (uom: string): number => {
        const rows = breakpointLookup?.[uom];
        if (!rows || rows.length === 0) {
            return 0;
        }

        const row = rows.find((candidate) => Number(candidate.price || 0) > 0) || rows[0];
        const price = Number(row?.price || 0);
        return Number.isFinite(price) && price > 0 ? price : 0;
    };

    const directPrice = resolveDirectPrice(normalizedTargetUom);
    if (directPrice > 0) {
        return directPrice;
    }

    if (normalizedTargetUom !== '' && normalizedTargetUom !== normalizedBaseUom) {
        const basePrice = resolveDirectPrice(normalizedBaseUom);
        if (basePrice > 0) {
            return convertComparePriceForUom(basePrice, unitConversions, normalizedTargetUom, normalizedBaseUom);
        }
    }

    return 0;
};

const resolveInventoryPolicyForUom = (
    policy: InventoryPolicy | undefined,
    policyByUom: Record<string, InventoryPolicy> | undefined,
    uom: string
): InventoryPolicy => {
    const normalizedUom = String(uom || '').trim().toUpperCase();
    const fromMap = normalizedUom !== '' ? policyByUom?.[normalizedUom] : undefined;
    return fromMap || policy || {};
};

const getPolicyPrecision = (policy: InventoryPolicy): number => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    if (!decimalAllowed) {
        return 0;
    }

    const rawPrecision = Number(policy.quantity_precision ?? 6);
    return normalizePolicyPrecision(rawPrecision);
};

const getPolicyStep = (policy: InventoryPolicy): number => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    const precision = getPolicyPrecision(policy);
    const fallback = decimalAllowed && precision > 0 ? (1 / (10 ** precision)) : 1;
    const raw = Number(policy.step_qty ?? fallback);
    if (!Number.isFinite(raw) || raw <= 0) {
        return fallback;
    }

    if (!decimalAllowed) {
        return Math.max(1, Math.round(raw));
    }

    return Math.max(fallback, normalizeQuantity(raw, true, precision));
};

const getPolicyMinNonZero = (policy: InventoryPolicy): number => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    const precision = getPolicyPrecision(policy);
    const step = getPolicyStep(policy);
    const raw = Number(policy.min_non_zero_qty ?? policy.min_qty ?? step);
    if (!Number.isFinite(raw) || raw <= 0) {
        return step;
    }

    return Math.max(step, normalizeQuantity(raw, decimalAllowed, precision));
};

const getPolicyMax = (policy: InventoryPolicy): number | null => {
    const rawValue = policy.max_qty;
    if (rawValue === null || typeof rawValue === 'undefined' || rawValue === '') {
        return null;
    }

    const raw = Number(rawValue);
    if (!Number.isFinite(raw)) {
        return null;
    }

    const decimalAllowed = policy.is_decimal_allowed === true;
    const precision = getPolicyPrecision(policy);
    return normalizeQuantity(Math.max(0, raw), decimalAllowed, precision);
};

const getInitialQuantityForPolicy = (policy: InventoryPolicy): number => {
    const allowZero = policy.allow_zero === true;
    if (allowZero) {
        return 0;
    }

    return getPolicyMinNonZero(policy);
};

const clampQuantityToPolicy = (requested: number, policy: InventoryPolicy): QuantityClampResult => {
    const decimalAllowed = policy.is_decimal_allowed === true;
    const precision = getPolicyPrecision(policy);
    const allowZero = policy.allow_zero === true;
    const step = getPolicyStep(policy);
    const minQty = allowZero ? 0 : getPolicyMinNonZero(policy);
    const minNonZero = getPolicyMinNonZero(policy);
    const maxQty = getPolicyMax(policy);
    const epsilon = 0.000001;

    const safeRequested = Number.isFinite(requested) ? Math.max(0, requested) : 0;
    let quantity = normalizeQuantity(safeRequested, decimalAllowed, precision);
    let reason = 'none';

    if (!decimalAllowed && Math.abs(quantity - safeRequested) > epsilon) {
        reason = 'decimal_not_allowed';
    }

    if (quantity > epsilon) {
        const positiveMin = allowZero ? minNonZero : minQty;
        if (quantity + epsilon < positiveMin) {
            quantity = positiveMin;
            reason = 'below_minimum';
        }

        if (step > epsilon) {
            let steppedQuantity = quantity;
            if (allowZero) {
                const multiples = Math.max(1, Math.round(quantity / step));
                steppedQuantity = multiples * step;
            } else {
                const base = positiveMin;
                const offset = Math.max(0, quantity - base);
                const multiples = Math.max(0, Math.round(offset / step));
                steppedQuantity = base + (multiples * step);
            }

            if (Math.abs(steppedQuantity - quantity) > epsilon) {
                quantity = steppedQuantity;
                if (reason === 'none') {
                    reason = 'invalid_step';
                }
            }
        }
    } else {
        quantity = 0;
        if (reason === 'none') {
            reason = 'zero_quantity';
        }
    }

    if (maxQty !== null && quantity > maxQty + epsilon) {
        if (allowZero) {
            const maxMultiples = Math.floor((maxQty / step) + epsilon);
            quantity = maxMultiples > 0 ? maxMultiples * step : 0;
        } else {
            quantity = maxQty;
        }

        reason = 'above_maximum';
    }

    if (maxQty !== null && maxQty <= epsilon && safeRequested > epsilon) {
        quantity = 0;
        reason = 'out_of_stock';
    }

    quantity = normalizeQuantity(quantity, decimalAllowed, precision);
    const adjusted = Math.abs(quantity - safeRequested) > epsilon;

    return {
        quantity,
        adjusted,
        reason: adjusted ? reason : 'none',
    };
};

const buildPolicyMessage = (
    result: QuantityClampResult,
    policy: InventoryPolicy,
    uomLabel: string,
    showStock: boolean = true
): string => {
    const step = getPolicyStep(policy);
    const maxQty = getPolicyMax(policy);
    const decimalAllowed = policy.is_decimal_allowed === true;
    const precision = getPolicyPrecision(policy);

    switch (result.reason) {
        case 'below_minimum':
            if (policy.step_mode_enabled === true) {
                return `MOQ increments enforced. Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed, precision)} ${uomLabel}.`;
            }
            return `Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed, precision)}.`;
        case 'invalid_step':
            return `Quantity adjusted to valid increments of ${formatQuantity(step, decimalAllowed, precision)} ${uomLabel}.`;
        case 'above_maximum':
            if (!showStock) {
                return 'Requested quantity is not currently available.';
            }
            if ((maxQty ?? 0) <= 0.000001 && policy.stock_fully_reserved_in_cart === true) {
                return 'All available stock is already in your cart.';
            }
            if (maxQty !== null) {
                return `Quantity adjusted to available maximum of ${formatQuantity(maxQty, decimalAllowed, precision)} ${uomLabel}.`;
            }
            return `Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed, precision)}.`;
        case 'out_of_stock':
            if (!showStock) {
                return 'This item is not currently available.';
            }
            if (policy.stock_fully_reserved_in_cart === true) {
                return 'All available stock is already in your cart.';
            }
            return `No stock available in ${uomLabel}.`;
        case 'decimal_not_allowed':
            return 'Whole quantities only for this product.';
        default:
            return `Quantity adjusted to ${formatQuantity(result.quantity, decimalAllowed, precision)}.`;
    }
};

const makeQtyRangeLabel = (minQty: number, maxQty: number | null): string => {
    if (minQty <= 1 && maxQty === 1) {
        return '1 Unit';
    }

    if (maxQty === null) {
        return `${minQty}+ Units`;
    }

    if (minQty === maxQty) {
        return `${minQty} Units`;
    }

    return `${minQty} - ${maxQty} Units`;
};

const getDefaultUom = (
    uomEnabled: boolean,
    availableUoms: string[],
    lookup: BreakpointLookup | undefined,
    baseUom: string
): string => {
    if (!uomEnabled) {
        return 'EA';
    }

    if (availableUoms.length > 0) {
        return availableUoms[0];
    }

    const lookupKeys = Object.keys(lookup || {});
    if (lookupKeys.length > 0) {
        return lookupKeys[0];
    }

    return baseUom || 'EA';
};

const normalizeDisplayMode = (value: unknown): 'full' | 'compact' => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'compact' ? 'compact' : 'full';
};

const normalizeCompactLayout = (value: unknown): CompactLayout => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'details_modal' || normalized === 'cart_details_modal'
        ? normalized
        : 'controls';
};

const normalizeNoticesHtml = (value: unknown): string => {
    return String(value || '').trim();
};

const isTruthy = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    const normalized = String(value || '').trim().toLowerCase();
    return ['1', 'true', 'yes', 'on'].includes(normalized);
};

const normalizeOptionalBoolean = (value: unknown): boolean | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    return isTruthy(value);
};

const normalizeCompactElementVisibility = (value: unknown): CompactElementVisibilityState => {
    const defaults: CompactElementVisibilityState = {
        productSku: false,
        itemPrice: true,
        calculatedPrice: true,
        quantityControls: true,
        addToCartButton: true,
        detailsButton: true,
        uomSelector: true,
        variationSelectors: true,
        serialSelector: true,
        messages: true,
        backInStockButton: true,
        draftButton: true,
        quoteButton: true,
        favouriteButton: true,
    };

    if (!value || typeof value !== 'object') {
        return defaults;
    }

    const rawVisibility = value as Record<string, unknown>;
    const legacyPriceSummary = normalizeOptionalBoolean(rawVisibility.priceSummary);
    if (legacyPriceSummary !== undefined) {
        defaults.itemPrice = legacyPriceSummary;
        defaults.calculatedPrice = legacyPriceSummary;
    }

    Object.keys(defaults).forEach((key) => {
        const normalized = normalizeOptionalBoolean(rawVisibility[key]);
        if (normalized !== undefined) {
            defaults[key as CompactElementVisibilityKey] = normalized;
        }
    });

    return defaults;
};

const normalizePriceMultiplier = (value: unknown): number => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1;
};

const normalizePriceSuffix = (value: unknown): string => {
    return String(value || '').replace(/\s+/g, ' ').trim();
};

const resolvePricingCurrencyCode = (
    breakpointLookup: BreakpointLookup | undefined,
    salesPrices: ProductPricing | undefined,
    selectedUom: string,
    quantityBreaksEnabled: boolean,
    quantity: number,
    defaultCurrencyCode: string
): string => {
    const fallbackCode = String(defaultCurrencyCode || '').trim().toUpperCase();
    const rows = breakpointLookup?.[selectedUom] || [];
    if (rows.length > 0) {
        const matchingRow = rows.find((row) => quantity >= Number(row.min_qty || 1)) || rows[0];
        const breakpointCode = String(matchingRow?.currency_code || '').trim().toUpperCase();
        if (breakpointCode !== '') {
            return breakpointCode;
        }
    }

    const salesRows = Object.values(salesPrices?.pricing?.[selectedUom] || {});
    if (salesRows.length > 0) {
        let selectedRow = salesRows[0];
        for (const priceEntry of salesRows) {
            const threshold = quantityBreaksEnabled ? Number(priceEntry.quantity || 1) : 1;
            if (quantity >= threshold) {
                selectedRow = priceEntry;
                continue;
            }
            break;
        }

        const salesRowCode = String(selectedRow?.currency_code || '').trim().toUpperCase();
        if (salesRowCode !== '') {
            return salesRowCode;
        }
    }

    return fallbackCode;
};

const resolvePricingCurrencySymbol = (
    breakpointLookup: BreakpointLookup | undefined,
    salesPrices: ProductPricing | undefined,
    selectedUom: string,
    quantityBreaksEnabled: boolean,
    quantity: number,
    defaultCurrencySymbol: string
): string => {
    const fallbackSymbol = String(defaultCurrencySymbol || '').trim();
    const rows = breakpointLookup?.[selectedUom] || [];
    if (rows.length > 0) {
        const matchingRow = rows.find((row) => quantity >= Number(row.min_qty || 1)) || rows[0];
        const breakpointSymbol = String(matchingRow?.currency_symbol || '').trim();
        if (breakpointSymbol !== '') {
            return breakpointSymbol;
        }
    }

    const salesRows = Object.values(salesPrices?.pricing?.[selectedUom] || {});
    if (salesRows.length > 0) {
        let selectedRow = salesRows[0];
        for (const priceEntry of salesRows) {
            const threshold = quantityBreaksEnabled ? Number(priceEntry.quantity || 1) : 1;
            if (quantity >= threshold) {
                selectedRow = priceEntry;
                continue;
            }
            break;
        }

        const salesRowSymbol = String(selectedRow?.currency_symbol || '').trim();
        if (salesRowSymbol !== '') {
            return salesRowSymbol;
        }
    }

    return fallbackSymbol;
};

const buildApiEndpoint = (path: string): string => {
    if (typeof window === 'undefined') {
        return `/wp-json/weka/v1/${path.replace(/^\/+/, '')}`;
    }

    const apiBaseRaw = String((window as any)?.wekaApiSettings?.api_endpoint_base || 'weka/v1');
    const apiBase = apiBaseRaw.replace(/^\/+|\/+$/g, '');
    const normalizedPath = path.replace(/^\/+/, '');

    return `/wp-json/${apiBase}/${normalizedPath}`;
};

const buildBackInStockTargetKey = (productId: number, variationId: number): string => {
    if (productId <= 0) {
        return '';
    }

    return `${productId}:${Math.max(0, variationId)}`;
};

const hasOutOfStockStatus = (policy: InventoryPolicy | undefined): boolean => {
    const normalizedStatus = String(policy?.normalized_status || '').trim().toLowerCase();
    return normalizedStatus === 'out_of_stock' || normalizedStatus === 'outofstock';
};

const shouldOfferBackInStock = ({
    featureEnabled,
    isVariableProduct,
    isVariationSelectionComplete,
    selectedVariation,
    selectedVariationId,
    activeIsPurchasable,
    activeInventoryPolicy,
}: {
    featureEnabled: boolean;
    isVariableProduct: boolean;
    isVariationSelectionComplete: boolean;
    selectedVariation: ProductVariation | null;
    selectedVariationId: number;
    activeIsPurchasable: boolean;
    activeInventoryPolicy: InventoryPolicy;
}): boolean => {
    if (!featureEnabled || !activeIsPurchasable) {
        return false;
    }

    const normalizedStatus = String(activeInventoryPolicy.normalized_status || '').trim().toLowerCase();
    if (
        activeInventoryPolicy.stock_fully_reserved_in_cart === true
        || activeInventoryPolicy.backorders_allowed === true
        || activeInventoryPolicy.is_on_backorder === true
        || normalizedStatus === 'backorder'
        || activeInventoryPolicy.can_purchase === true
    ) {
        return false;
    }

    if (isVariableProduct) {
        return isVariationSelectionComplete
            && selectedVariationId > 0
            && selectedVariation !== null
            && selectedVariation.isPurchasable !== false
            && (
                selectedVariation.isInStock === false
                || hasOutOfStockStatus(activeInventoryPolicy)
            );
    }

    return hasOutOfStockStatus(activeInventoryPolicy);
};

const normalizeVariationAttributeKey = (value: unknown): string => {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === '') {
        return '';
    }

    if (raw.startsWith('attribute_')) {
        return raw;
    }

    return `attribute_${raw}`;
};

const normalizeVariationValue = (value: unknown): string => {
    return String(value || '').trim();
};

const normalizeVariationAttributesMap = (raw: unknown): Record<string, string> => {
    if (!raw || typeof raw !== 'object') {
        return {};
    }

    const normalized: Record<string, string> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
        const normalizedKey = normalizeVariationAttributeKey(key);
        if (normalizedKey === '') {
            return;
        }
        normalized[normalizedKey] = normalizeVariationValue(value);
    });

    return normalized;
};

const normalizeProductPricing = (raw: unknown): ProductPricing | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const source = raw as Record<string, unknown>;
    const pricing = source.pricing;
    const quantityBreaks = source.quantity_breaks;
    if ((!pricing || typeof pricing !== 'object') && (!quantityBreaks || typeof quantityBreaks !== 'object')) {
        return null;
    }

    return {
        pricing: (pricing && typeof pricing === 'object') ? (pricing as ProductPricing['pricing']) : {},
        quantity_breaks: (quantityBreaks && typeof quantityBreaks === 'object')
            ? (quantityBreaks as ProductPricing['quantity_breaks'])
            : {},
    };
};

const normalizeBreakpointLookup = (raw: unknown): BreakpointLookup | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    return raw as BreakpointLookup;
};

const normalizeInventoryPolicy = (raw: unknown): InventoryPolicy | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    return raw as InventoryPolicy;
};

const normalizeInventoryPolicyMap = (raw: unknown): Record<string, InventoryPolicy> | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    return raw as Record<string, InventoryPolicy>;
};

const normalizeAvailableUoms = (raw: unknown): string[] => {
    if (!Array.isArray(raw)) {
        return [];
    }

    const seen = new Set<string>();
    const normalized: string[] = [];
    raw.forEach((entry) => {
        const value = String(entry || '').trim().toUpperCase();
        if (value !== '' && !seen.has(value)) {
            seen.add(value);
            normalized.push(value);
        }
    });

    return normalized;
};

const normalizeUnitConversions = (raw: unknown): UnitConversion[] => {
    if (!Array.isArray(raw)) {
        return [];
    }

    return raw
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry) => entry as UnitConversion);
};

const normalizeVariationAttribute = (raw: unknown): VariationAttribute | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const source = raw as Record<string, unknown>;
    const key = normalizeVariationAttributeKey(source.key ?? source.name);
    if (key === '') {
        return null;
    }

    const name = String(source.name || '').trim();
    const fallbackLabel = key.replace(/^attribute_/, '').replace(/^pa_/, '').replace(/[_-]+/g, ' ').trim();
    const label = String(source.label || name || fallbackLabel || key).trim();

    const options: VariationOption[] = [];
    const seenValues = new Set<string>();
    const rawOptions = Array.isArray(source.options) ? source.options : [];
    rawOptions.forEach((option) => {
        if (option && typeof option === 'object') {
            const optionRow = option as Record<string, unknown>;
            const optionValue = normalizeVariationValue(optionRow.value);
            if (optionValue === '' || seenValues.has(optionValue)) {
                return;
            }

            seenValues.add(optionValue);
            const optionLabel = String(optionRow.label || optionValue).trim();
            options.push({
                value: optionValue,
                label: optionLabel !== '' ? optionLabel : optionValue,
            });
            return;
        }

        const optionValue = normalizeVariationValue(option);
        if (optionValue === '' || seenValues.has(optionValue)) {
            return;
        }

        seenValues.add(optionValue);
        options.push({
            value: optionValue,
            label: optionValue,
        });
    });

    return {
        key,
        name,
        label,
        options,
    };
};

const normalizeVariation = (raw: unknown): ProductVariation | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const source = raw as Record<string, unknown>;
    const id = Number(source.id ?? source.variation_id ?? 0);
    if (!Number.isFinite(id) || id <= 0) {
        return null;
    }

    return {
        id,
        sku: String(source.sku ?? '').trim(),
        attributes: normalizeVariationAttributesMap(source.attributes),
        isInStock: source.isInStock === undefined ? undefined : isTruthy(source.isInStock),
        isPurchasable: source.isPurchasable === undefined ? undefined : isTruthy(source.isPurchasable),
        pricingUnavailableMessage: String(source.pricingUnavailableMessage ?? source.pricing_unavailable_message ?? '').trim(),
        price: Number(source.price ?? 0),
        regularPrice: Number(source.regularPrice ?? source.regular_price ?? 0),
        comparePrice: Number(source.comparePrice ?? source.compare_price ?? source.regularPrice ?? source.regular_price ?? 0),
        comparePriceSource: String(source.comparePriceSource ?? source.compare_price_source ?? '').trim(),
        basePrice: Number(source.basePrice ?? source.base_price ?? source.price ?? 0),
        salesPrices: normalizeProductPricing(source.salesPrices ?? source.sales_prices),
        breakpointLookup: normalizeBreakpointLookup(source.breakpointLookup ?? source.breakpoint_lookup),
        baseUom: String(source.baseUom ?? source.base_uom ?? 'EA').trim().toUpperCase() || 'EA',
        uomEnabled: source.uomEnabled === undefined ? undefined : isTruthy(source.uomEnabled),
        availableUoms: normalizeAvailableUoms(source.availableUoms ?? source.available_uoms),
        unitConversions: normalizeUnitConversions(source.unitConversions ?? source.unit_conversions),
        inventoryPolicy: normalizeInventoryPolicy(source.inventoryPolicy ?? source.inventory_policy),
        inventoryPolicyByUom: normalizeInventoryPolicyMap(source.inventoryPolicyByUom ?? source.inventory_policy_by_uom),
        serialEnabled: source.serialEnabled === undefined ? undefined : isTruthy(source.serialEnabled),
        serialForceBuyAll: source.serialForceBuyAll === undefined ? undefined : isTruthy(source.serialForceBuyAll),
        serialDisplayMode: String(source.serialDisplayMode ?? source.serial_display_mode ?? '').trim(),
        serialOptionsEndpoint: String(source.serialOptionsEndpoint ?? source.serial_options_endpoint ?? '').trim(),
        stockStatus: String(source.stockStatus ?? source.stock_status ?? '').trim(),
        stockQuantity: source.stockQuantity === undefined || source.stockQuantity === null
            ? null
            : Number(source.stockQuantity),
        isOnBackorder: source.isOnBackorder === undefined ? undefined : isTruthy(source.isOnBackorder),
    };
};

const buildSerialOptionsEndpointForProduct = (endpoint: string, productId: number): string => {
    const normalizedEndpoint = String(endpoint || '').trim();
    if (normalizedEndpoint === '' || productId <= 0) {
        return '';
    }

    try {
        if (typeof window === 'undefined') {
            return normalizedEndpoint.replace(/serialnumber_stock_options\/\d+/, `serialnumber_stock_options/${productId}`);
        }

        const url = new URL(normalizedEndpoint, window.location.origin);
        url.pathname = url.pathname.replace(/serialnumber_stock_options\/\d+/, `serialnumber_stock_options/${productId}`);
        return `${url.pathname}${url.search}`;
    } catch {
        return normalizedEndpoint.replace(/serialnumber_stock_options\/\d+/, `serialnumber_stock_options/${productId}`);
    }
};

const getSerialOptionKey = (option: SerialOption): string => {
    return [
        String(option.serial_number || '').trim(),
        String(option.warehouse_id || '').trim(),
        String(option.location_id || '').trim(),
    ].join('|');
};

const normalizeSerialOption = (raw: any): SerialOption | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const serialNumber = String(raw.serial_number || '').trim();
    if (serialNumber === '') {
        return null;
    }

    const quantity = Number(raw.quantity || 0);
    const safeQuantity = Number.isFinite(quantity) ? Math.max(0, quantity) : 0;

    return {
        serial_number: serialNumber,
        warehouse_id: String(raw.warehouse_id || '').trim(),
        location_id: String(raw.location_id || '').trim(),
        quantity: safeQuantity,
        expiration_date: String(raw.expiration_date || '').trim(),
        label: String(raw.label || '').trim(),
    };
};

const WekaAddToCart: React.FC<WekaAddToCartProps> = (props) => {
    const isCompactMode = normalizeDisplayMode(props.displayMode) === 'compact';
    const compactLayout = normalizeCompactLayout(props.compactLayout);
    const isCompactDetailsMode = isCompactMode && compactLayout === 'details_modal';
    const isCompactCartDetailsMode = isCompactMode && compactLayout === 'cart_details_modal';
    const usesCompactDetailsModal = isCompactDetailsMode || isCompactCartDetailsMode;
    const compactElementVisibility = useMemo(
        () => normalizeCompactElementVisibility(props.compactElementVisibility),
        [props.compactElementVisibility]
    );
    const canShowCompactElement = (key: CompactElementVisibilityKey): boolean => {
        return !isCompactMode || compactElementVisibility[key] !== false;
    };
    const compactDetailsButtonLabel = String(props.compactDetailsButtonLabel || 'Details').trim() || 'Details';
    const [selectedWarehouse, setSelectedWarehouse] = useState(() => String(props.selectedWarehouseId || '').trim());
    const [compactBreaksOpen, setCompactBreaksOpen] = useState(() => !isCompactMode);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isDetailsModalLoading, setIsDetailsModalLoading] = useState(false);
    const [detailsModalError, setDetailsModalError] = useState('');
    const [detailsModalResponse, setDetailsModalResponse] = useState<ProductDetailResponse>(emptyProductDetailResponse);
    const [quantityNotice, setQuantityNotice] = useState('');
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [isDraftSelectorOpen, setIsDraftSelectorOpen] = useState(false);
    const [draftItemPayload, setDraftItemPayload] = useState<DraftOrderItemInput | null>(null);
    const [isQuoteSelectorOpen, setIsQuoteSelectorOpen] = useState(false);
    const [quoteItemPayload, setQuoteItemPayload] = useState<QuoteItemInput | null>(null);
    const addToCartInFlightRef = useRef(false);
    const [woocommerceNoticesHtml, setWoocommerceNoticesHtml] = useState('');
    const [isBackInStockSubmitting, setIsBackInStockSubmitting] = useState(false);
    const [subscribedBackInStockTargets, setSubscribedBackInStockTargets] = useState<Set<string>>(
        () => new Set(resolveBackInStockTargetKeys(props.initialBackInStockTargetKeys))
    );
    const [backInStockNotice, setBackInStockNotice] = useState('');
    const detailsButtonRef = useRef<HTMLButtonElement | null>(null);
    const detailsCloseButtonRef = useRef<HTMLButtonElement | null>(null);
    const wasDetailsModalOpenRef = useRef(false);
    const baseSalesPrices = props.salesPrices ?? {pricing: {}, quantity_breaks: {}};
    const showPrice = normalizeOptionalBoolean(props.showPrice) !== false;
    const allowAddToCart = normalizeOptionalBoolean(props.allowAddToCart) !== false;
    const showStock = normalizeOptionalBoolean(props.showStock) !== false;
    const isEnquiryProduct = isTruthy(props.isEnquiryProduct);
    const enquiryButtonText = String(props.enquiryButtonText || 'Enquire Now').trim() || 'Enquire Now';
    const currencyDisplayMode = normalizeCurrencyDisplayMode(props.currencyDisplayMode ?? props.showCurrencyCode);
    const priceDisplayMultiplier = normalizePriceMultiplier(props.priceDisplayMultiplier);
    const priceIncludingTaxMultiplier = normalizePriceMultiplier(props.priceIncludingTaxMultiplier);
    const priceExcludingTaxMultiplier = normalizePriceMultiplier(props.priceExcludingTaxMultiplier);
    const priceDisplaySuffix = normalizePriceSuffix(props.priceDisplaySuffix);
    const priceDisplaySuffixTemplate = normalizePriceSuffix(props.priceDisplaySuffixTemplate);
    const toDisplayPrice = (amount: string | number | undefined): number => {
        const parsedAmount = typeof amount === 'number' ? amount : parseFloat(String(amount || '0'));
        const safeAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
        return safeAmount * priceDisplayMultiplier;
    };
    const buildDisplayPriceSuffix = (
        amount: string | number | undefined,
        currencyCode: string,
        currencySymbol: string
    ): string => {
        const template = priceDisplaySuffixTemplate;
        if (template === '') {
            return priceDisplaySuffix;
        }

        if (!template.includes('{price_including_tax}') && !template.includes('{price_excluding_tax}')) {
            return priceDisplaySuffix || template;
        }

        const parsedAmount = typeof amount === 'number' ? amount : parseFloat(String(amount || '0'));
        const safeAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;

        return normalizePriceSuffix(template
            .split('{price_including_tax}')
            .join(formatMoneyWithCurrencyCode(safeAmount * priceIncludingTaxMultiplier, currencyCode, currencyDisplayMode, currencySymbol))
            .split('{price_excluding_tax}')
            .join(formatMoneyWithCurrencyCode(safeAmount * priceExcludingTaxMultiplier, currencyCode, currencyDisplayMode, currencySymbol)));
    };
    const formatDisplayMoney = (
        amount: string | number | undefined,
        currencyCode: string,
        currencySymbol: string,
        includeSuffix = true
    ): string => {
        const formattedAmount = formatMoneyWithCurrencyCode(toDisplayPrice(amount), currencyCode, currencyDisplayMode, currencySymbol);
        if (!includeSuffix) {
            return formattedAmount;
        }

        const suffix = buildDisplayPriceSuffix(amount, currencyCode, currencySymbol);
        return suffix !== '' ? `${formattedAmount} ${suffix}` : formattedAmount;
    };
    const quantityBreaksEnabled = props.quantityBreaksEnabled !== false;
    const baseUomEnabled = props.uomEnabled === true;
    const baseSerialEnabled = isTruthy(props.serialEnabled);
    const baseSerialForceBuyAll = baseSerialEnabled && isTruthy(props.serialForceBuyAll);
    const baseSerialDisplayMode = String(props.serialDisplayMode || 'dropdown').trim().toLowerCase() === 'table'
        ? 'table'
        : 'dropdown';
    const baseSerialOptionsEndpoint = String(props.serialOptionsEndpoint || '').trim();
    const [serialOptions, setSerialOptions] = useState<SerialOption[]>([]);
    const [selectedSerialKey, setSelectedSerialKey] = useState('');
    const [serialLoading, setSerialLoading] = useState(false);
    const [serialError, setSerialError] = useState('');

    const normalizedVariationAttributes = useMemo<VariationAttribute[]>(() => {
        const rawAttributes = Array.isArray(props.variationAttributes) ? props.variationAttributes : [];
        return rawAttributes
            .map((attribute) => normalizeVariationAttribute(attribute))
            .filter((attribute): attribute is VariationAttribute => attribute !== null);
    }, [props.variationAttributes]);

    const normalizedAvailableVariations = useMemo<ProductVariation[]>(() => {
        const rawVariations = Array.isArray(props.availableVariations) ? props.availableVariations : [];
        return rawVariations
            .map((variation) => normalizeVariation(variation))
            .filter((variation): variation is ProductVariation => variation !== null);
    }, [props.availableVariations]);

    const normalizedDefaultVariationAttributes = useMemo<Record<string, string>>(
        () => normalizeVariationAttributesMap(props.defaultVariationAttributes || {}),
        [props.defaultVariationAttributes]
    );

    const isVariableProduct = isTruthy(props.isVariableProduct)
        || normalizedVariationAttributes.length > 0
        || normalizedAvailableVariations.length > 0;

    const [selectedVariationAttributes, setSelectedVariationAttributes] = useState<Record<string, string>>({});

    useEffect(() => {
        setCompactBreaksOpen(!isCompactMode);
    }, [isCompactMode, props.product.id]);

    useEffect(() => {
        setIsDetailsModalOpen(false);
        setIsDetailsModalLoading(false);
        setDetailsModalError('');
        setDetailsModalResponse(emptyProductDetailResponse);
    }, [props.product.id]);

    useEffect(() => {
        if (!usesCompactDetailsModal || !isDetailsModalOpen) {
            return;
        }

        if (detailsModalResponse.results.length > 0) {
            return;
        }

        let isActive = true;
        setIsDetailsModalLoading(true);
        setDetailsModalError('');

        fetchCachedProductDetail(String(props.product.id || '').trim())
            .then((response) => {
                if (!isActive) {
                    return;
                }

                setDetailsModalResponse(response);
            })
            .catch((error) => {
                if (!isActive) {
                    return;
                }

                const message = error instanceof Error ? error.message : 'Unable to load this product right now.';
                setDetailsModalError(message);
            })
            .finally(() => {
                if (isActive) {
                    setIsDetailsModalLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [
        usesCompactDetailsModal,
        isDetailsModalOpen,
        detailsModalResponse.results.length,
        props.product.id,
    ]);

    useEffect(() => {
        if (!usesCompactDetailsModal || !isDetailsModalOpen || typeof document === 'undefined') {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsDetailsModalOpen(false);
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [usesCompactDetailsModal, isDetailsModalOpen]);

    useEffect(() => {
        if (!usesCompactDetailsModal || !isDetailsModalOpen || typeof window === 'undefined') {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            detailsCloseButtonRef.current?.focus();
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [usesCompactDetailsModal, isDetailsModalOpen]);

    useEffect(() => {
        if (!usesCompactDetailsModal || typeof window === 'undefined') {
            wasDetailsModalOpenRef.current = isDetailsModalOpen;
            return;
        }

        if (wasDetailsModalOpenRef.current && !isDetailsModalOpen) {
            window.requestAnimationFrame(() => {
                detailsButtonRef.current?.focus();
            });
        }

        wasDetailsModalOpenRef.current = isDetailsModalOpen;
    }, [usesCompactDetailsModal, isDetailsModalOpen]);

    useEffect(() => {
        setSelectedWarehouse(String(props.selectedWarehouseId || '').trim());
    }, [props.selectedWarehouseId]);

    useEffect(() => {
        if (normalizedVariationAttributes.length === 0) {
            setSelectedVariationAttributes({});
            return;
        }

        setSelectedVariationAttributes((previousSelection) => {
            const nextSelection: Record<string, string> = {};

            normalizedVariationAttributes.forEach((attribute) => {
                const optionSet = new Set(attribute.options.map((option) => normalizeVariationValue(option.value)));
                const previousValue = normalizeVariationValue(previousSelection[attribute.key]);
                const defaultValue = normalizeVariationValue(normalizedDefaultVariationAttributes[attribute.key]);

                if (previousValue !== '' && (optionSet.size === 0 || optionSet.has(previousValue))) {
                    nextSelection[attribute.key] = previousValue;
                    return;
                }

                if (defaultValue !== '' && (optionSet.size === 0 || optionSet.has(defaultValue))) {
                    nextSelection[attribute.key] = defaultValue;
                    return;
                }

                nextSelection[attribute.key] = '';
            });

            return nextSelection;
        });
    }, [normalizedVariationAttributes, normalizedDefaultVariationAttributes]);

    const selectedVariationPayload = useMemo<Record<string, string>>(() => {
        if (!isVariableProduct || normalizedVariationAttributes.length === 0) {
            return {};
        }

        const payload: Record<string, string> = {};
        normalizedVariationAttributes.forEach((attribute) => {
            const selectedValue = normalizeVariationValue(selectedVariationAttributes[attribute.key]);
            if (selectedValue !== '') {
                payload[attribute.key] = selectedValue;
            }
        });

        return payload;
    }, [isVariableProduct, normalizedVariationAttributes, selectedVariationAttributes]);

    const isVariationSelectionComplete = useMemo(() => {
        if (!isVariableProduct || normalizedVariationAttributes.length === 0) {
            return true;
        }

        return normalizedVariationAttributes.every((attribute) => {
            return normalizeVariationValue(selectedVariationPayload[attribute.key]) !== '';
        });
    }, [isVariableProduct, normalizedVariationAttributes, selectedVariationPayload]);

    const matchingVariations = useMemo<ProductVariation[]>(() => {
        if (!isVariableProduct || normalizedAvailableVariations.length === 0) {
            return [];
        }

        return normalizedAvailableVariations.filter((variation) => {
            return normalizedVariationAttributes.every((attribute) => {
                const selectedValue = normalizeVariationValue(selectedVariationPayload[attribute.key]);
                if (selectedValue === '') {
                    return true;
                }

                const variationValue = normalizeVariationValue(variation.attributes[attribute.key]);
                return variationValue === '' || variationValue === selectedValue;
            });
        });
    }, [isVariableProduct, normalizedAvailableVariations, normalizedVariationAttributes, selectedVariationPayload]);

    const selectedVariation = useMemo<ProductVariation | null>(() => {
        if (!isVariableProduct || !isVariationSelectionComplete || matchingVariations.length === 0) {
            return null;
        }

        const availableMatch = matchingVariations.find((variation) => {
            return variation.isInStock !== false && variation.isPurchasable !== false;
        });

        return availableMatch || matchingVariations[0] || null;
    }, [isVariableProduct, isVariationSelectionComplete, matchingVariations]);

    const selectedVariationId = selectedVariation?.id ?? 0;
    const activeProductSku = String(selectedVariation?.sku || props.product.sku || '').trim();
    const showVariationSelectors = isVariableProduct
        && normalizedVariationAttributes.length > 0
        && canShowCompactElement('variationSelectors');
    const isVariationSelectionValid = !isVariableProduct
        || (
            isVariationSelectionComplete
            && selectedVariation !== null
            && selectedVariation.isInStock !== false
            && selectedVariation.isPurchasable !== false
        );

    const variationSelectionMessage = useMemo(() => {
        if (!isVariableProduct || normalizedVariationAttributes.length === 0) {
            return '';
        }

        if (!isVariationSelectionComplete) {
            return 'Select product options to enable add to cart.';
        }

        if (!selectedVariation || selectedVariation.isInStock === false || selectedVariation.isPurchasable === false) {
            return 'Selected option combination is unavailable.';
        }

        return '';
    }, [isVariableProduct, normalizedVariationAttributes.length, isVariationSelectionComplete, selectedVariation]);

    const isVariationOptionSelectable = (attributeKey: string, optionValue: string): boolean => {
        if (!isVariableProduct || normalizedAvailableVariations.length === 0) {
            return true;
        }

        const normalizedAttributeKey = normalizeVariationAttributeKey(attributeKey);
        const normalizedOptionValue = normalizeVariationValue(optionValue);
        if (normalizedAttributeKey === '' || normalizedOptionValue === '') {
            return true;
        }

        return normalizedAvailableVariations.some((variation) => {
            if (variation.isInStock === false || variation.isPurchasable === false) {
                return false;
            }

            return normalizedVariationAttributes.every((attribute) => {
                const targetValue = attribute.key === normalizedAttributeKey
                    ? normalizedOptionValue
                    : normalizeVariationValue(selectedVariationPayload[attribute.key]);
                if (targetValue === '') {
                    return true;
                }

                const variationValue = normalizeVariationValue(variation.attributes[attribute.key]);
                return variationValue === '' || variationValue === targetValue;
            });
        });
    };

    const activeBasePrice = useMemo(() => {
        const variationBasePrice = Number(selectedVariation?.basePrice ?? 0);
        if (Number.isFinite(variationBasePrice) && variationBasePrice > 0) {
            return variationBasePrice;
        }

        const variationPrice = Number(selectedVariation?.price ?? 0);
        if (Number.isFinite(variationPrice) && variationPrice > 0) {
            return variationPrice;
        }

        return props.basePrice;
    }, [selectedVariation, props.basePrice]);

    const activeRawComparePrice = useMemo(() => {
        const variationComparePrice = Number(selectedVariation?.comparePrice ?? 0);
        if (Number.isFinite(variationComparePrice) && variationComparePrice > 0) {
            return variationComparePrice;
        }

        const variationRegularPrice = Number(selectedVariation?.regularPrice ?? 0);
        if (Number.isFinite(variationRegularPrice) && variationRegularPrice > 0) {
            return variationRegularPrice;
        }

        const productComparePrice = Number(props.comparePrice ?? 0);
        if (Number.isFinite(productComparePrice) && productComparePrice > 0) {
            return productComparePrice;
        }

        return 0;
    }, [selectedVariation, props.comparePrice]);

    const activeComparePriceSource = useMemo(() => {
        const variationSource = String(selectedVariation?.comparePriceSource || '').trim().toLowerCase();
        if (variationSource !== '') {
            return variationSource;
        }

        const productSource = String(props.comparePriceSource || '').trim().toLowerCase();
        return productSource !== '' ? productSource : COMPARE_PRICE_SOURCE_REGULAR;
    }, [selectedVariation, props.comparePriceSource]);

    const activeSalesPrices = useMemo<ProductPricing>(() => {
        return selectedVariation?.salesPrices ?? baseSalesPrices;
    }, [selectedVariation, baseSalesPrices]);

    const activeBreakpointLookup = useMemo<BreakpointLookup | undefined>(() => {
        return selectedVariation?.breakpointLookup ?? props.breakpointLookup;
    }, [selectedVariation, props.breakpointLookup]);
    const activePricingUnavailableMessage = useMemo(() => {
        const variationMessage = String(selectedVariation?.pricingUnavailableMessage || '').trim();
        if (variationMessage !== '') {
            return variationMessage;
        }

        return String(props.pricingUnavailableMessage || '').trim();
    }, [selectedVariation, props.pricingUnavailableMessage]);
    const effectiveShowPrice = showPrice && activePricingUnavailableMessage === '';
    const showQuantityBreaksTableSetting = effectiveShowPrice && props.showQuantityBreaksTable !== false;

    const activeBaseUom = useMemo(() => {
        const candidate = String(selectedVariation?.baseUom || props.baseUom || 'EA').trim().toUpperCase();
        return candidate === '' ? 'EA' : candidate;
    }, [selectedVariation, props.baseUom]);

    const activeUnitConversions = useMemo<UnitConversion[]>(() => {
        const variationConversions = selectedVariation?.unitConversions;
        if (variationConversions && variationConversions.length > 0) {
            return variationConversions;
        }

        return Array.isArray(props.unitConversions) ? props.unitConversions : [];
    }, [selectedVariation, props.unitConversions]);

    const activeAvailableUoms = useMemo<string[]>(() => {
        const variationUoms = selectedVariation?.availableUoms;
        if (variationUoms && variationUoms.length > 0) {
            return variationUoms;
        }

        return Array.isArray(props.availableUoms) ? props.availableUoms : [];
    }, [selectedVariation, props.availableUoms]);

    const activeUomEnabled = isVariableProduct
        ? (selectedVariation ? selectedVariation.uomEnabled === true : baseUomEnabled)
        : baseUomEnabled;

    const activeInventoryPolicySource = useMemo<InventoryPolicy | undefined>(() => {
        return selectedVariation?.inventoryPolicy ?? props.inventoryPolicy;
    }, [selectedVariation, props.inventoryPolicy]);

    const activeInventoryPolicyByUomSource = useMemo<Record<string, InventoryPolicy> | undefined>(() => {
        return selectedVariation?.inventoryPolicyByUom ?? props.inventoryPolicyByUom;
    }, [selectedVariation, props.inventoryPolicyByUom]);

    const activeSerialEnabled = isVariableProduct
        ? (selectedVariation ? selectedVariation.serialEnabled === true : false)
        : baseSerialEnabled;
    const activeSerialForceBuyAll = activeSerialEnabled
        && (selectedVariation?.serialForceBuyAll ?? baseSerialForceBuyAll);
    const activeSerialDisplayMode = String(selectedVariation?.serialDisplayMode || baseSerialDisplayMode).trim().toLowerCase() === 'table'
        ? 'table'
        : 'dropdown';
    const activeSerialOptionsEndpointSeed = String(selectedVariation?.serialOptionsEndpoint || baseSerialOptionsEndpoint).trim();
    const activeIsPurchasable = isVariableProduct
        ? (selectedVariation ? selectedVariation.isPurchasable !== false : props.isPurchasable !== false)
        : props.isPurchasable !== false;

    const resolvedSerialOptionsEndpoint = useMemo(() => {
        if (activeSerialOptionsEndpointSeed === '') {
            return '';
        }

        const targetId = isVariableProduct ? selectedVariationId : props.product.id;
        if (targetId <= 0) {
            return '';
        }

        return buildSerialOptionsEndpointForProduct(activeSerialOptionsEndpointSeed, targetId);
    }, [activeSerialOptionsEndpointSeed, isVariableProduct, selectedVariationId, props.product.id]);

    const resolvedUoms = useMemo(() => {
        if (!activeUomEnabled) {
            return ['EA'];
        }

        const merged: string[] = [];
        const pushIfMissing = (value: string) => {
            const normalized = String(value || '').trim().toUpperCase();
            if (normalized !== '' && !merged.includes(normalized)) {
                merged.push(normalized);
            }
        };

        for (const uom of activeAvailableUoms) {
            pushIfMissing(uom);
        }

        for (const uom of Object.keys(activeSalesPrices.pricing || {})) {
            pushIfMissing(uom);
        }

        for (const row of activeUnitConversions) {
            if (String(row.to).toUpperCase() === activeBaseUom) {
                pushIfMissing(row.from);
            }
        }

        for (const uom of Object.keys(activeInventoryPolicyByUomSource || {})) {
            pushIfMissing(uom);
        }

        if (!merged.includes('EA')) {
            merged.unshift('EA');
        }

        return merged;
    }, [activeUomEnabled, activeAvailableUoms, activeSalesPrices.pricing, activeUnitConversions, activeBaseUom, activeInventoryPolicyByUomSource]);

    const [selectedUom, setSelectedUom] = useState(() =>
        getDefaultUom(activeUomEnabled, resolvedUoms, activeBreakpointLookup, activeBaseUom)
    );

    useEffect(() => {
        const nextUom = getDefaultUom(activeUomEnabled, resolvedUoms, activeBreakpointLookup, activeBaseUom);
        if (!resolvedUoms.includes(selectedUom)) {
            setSelectedUom(nextUom);
        }
    }, [activeUomEnabled, resolvedUoms, activeBreakpointLookup, activeBaseUom, selectedUom]);

    useEffect(() => {
        if (!activeSerialEnabled) {
            setSerialOptions([]);
            setSelectedSerialKey('');
            setSerialError('');
            setSerialLoading(false);
            return;
        }

        if (isVariableProduct && selectedVariationId <= 0) {
            setSerialOptions([]);
            setSelectedSerialKey('');
            setSerialError('Select product options to load serial numbers.');
            setSerialLoading(false);
            return;
        }

        if (resolvedSerialOptionsEndpoint === '') {
            setSerialOptions([]);
            setSelectedSerialKey('');
            setSerialError('Serial options endpoint is not configured.');
            setSerialLoading(false);
            return;
        }

        const controller = new AbortController();
        const loadSerialOptions = async () => {
            setSerialLoading(true);
            setSerialError('');

            try {
                const response = await fetch(resolvedSerialOptionsEndpoint, {
                    credentials: 'include',
                    signal: controller.signal,
                    headers: {
                        Accept: 'application/json',
                    },
                });

                const data = await response.json();
                if (!response.ok) {
                    throw new Error(String(data?.message || data?.error || 'Failed to load serial options.'));
                }

                const normalizedOptions = Array.isArray(data?.options)
                    ? data.options
                        .map((option: any) => normalizeSerialOption(option))
                        .filter((option: SerialOption | null): option is SerialOption => option !== null && option.quantity > 0)
                    : [];

                setSerialOptions(normalizedOptions);
                setSelectedSerialKey((previousKey) => {
                    if (previousKey === '') {
                        return '';
                    }

                    const stillExists = normalizedOptions.some((option) => getSerialOptionKey(option) === previousKey);
                    return stillExists ? previousKey : '';
                });
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }

                const message = error instanceof Error ? error.message : 'Failed to load serial options.';
                setSerialOptions([]);
                setSelectedSerialKey('');
                setSerialError(message);
            } finally {
                if (!controller.signal.aborted) {
                    setSerialLoading(false);
                }
            }
        };

        void loadSerialOptions();
        return () => controller.abort();
    }, [activeSerialEnabled, isVariableProduct, selectedVariationId, resolvedSerialOptionsEndpoint]);

    const selectedSerialOption = useMemo<SerialOption | null>(() => {
        if (selectedSerialKey === '') {
            return null;
        }

        return serialOptions.find((option) => getSerialOptionKey(option) === selectedSerialKey) || null;
    }, [serialOptions, selectedSerialKey]);

    const activeInventoryPolicy = useMemo(
        () => resolveInventoryPolicyForUom(activeInventoryPolicySource, activeInventoryPolicyByUomSource, selectedUom),
        [activeInventoryPolicySource, activeInventoryPolicyByUomSource, selectedUom]
    );

    const quantityPolicy = useMemo<InventoryPolicy>(() => {
        if (!selectedSerialOption || activeSerialForceBuyAll) {
            return activeInventoryPolicy;
        }

        const serialQuantity = Math.max(0, Number(selectedSerialOption.quantity || 0));
        const policyMax = getPolicyMax(activeInventoryPolicy);
        const resolvedMax = policyMax === null ? serialQuantity : Math.min(policyMax, serialQuantity);

        return {
            ...activeInventoryPolicy,
            max_qty: resolvedMax,
        };
    }, [activeInventoryPolicy, selectedSerialOption, activeSerialForceBuyAll]);

    const [selectedQuantity, setSelectedQuantity] = useState<number>(() => getInitialQuantityForPolicy(quantityPolicy));

    useEffect(() => {
        setSelectedQuantity((prevQuantity) => {
            if (activeSerialForceBuyAll && selectedSerialOption) {
                const forcedQuantity = Number(selectedSerialOption.quantity || 0);
                return Number.isFinite(forcedQuantity) ? Math.max(0, forcedQuantity) : 0;
            }

            const requestedQuantity = Number.isFinite(prevQuantity) ? prevQuantity : getInitialQuantityForPolicy(quantityPolicy);
            const result = clampQuantityToPolicy(requestedQuantity, quantityPolicy);
            return result.quantity;
        });
    }, [quantityPolicy, activeSerialForceBuyAll, selectedSerialOption]);

    const {pricing} = usePrice(
        activeBasePrice,
        activeUnitConversions,
        activeSalesPrices,
        activeBreakpointLookup,
        quantityBreaksEnabled,
        activeBaseUom
    );

    const breakpointRows = useMemo<BreakpointRowView[]>(() => {
        let rows = (activeBreakpointLookup?.[selectedUom] || []).slice();
        if (rows.length === 0) {
            const normalizedBaseUom = activeBaseUom;
            const baseRows = (activeBreakpointLookup?.[normalizedBaseUom] || []).slice();
            const conversion = activeUnitConversions.find((entry) =>
                String(entry.from || '').toUpperCase() === selectedUom
                && String(entry.to || '').toUpperCase() === normalizedBaseUom
            );
            const factor = Number(conversion?.factor || 0);

            if (baseRows.length > 0 && factor > 0) {
                rows = baseRows.map((row) => ({
                    ...row,
                    price: Number(row.price || 0) * factor,
                    uom: selectedUom,
                }));
            }
        }

        if (rows.length === 0) {
            return [];
        }

        const baseUnitPrice = Number(rows[0]?.price || 0);
        return rows.map((row, index) => {
            const minQty = Math.max(1, Number(row.min_qty || 1));
            const nextRow = rows[index + 1];
            const nextMin = nextRow ? Math.max(1, Number(nextRow.min_qty || minQty + 1)) : null;
            const maxQty = nextMin !== null ? Math.max(minQty, nextMin - 1) : null;
            const unitPrice = Number(row.price || 0);
            const savingsPercent = baseUnitPrice > 0
                ? Math.max(0, Math.round(((baseUnitPrice - unitPrice) / baseUnitPrice) * 100))
                : 0;

            return {
                ...row,
                min_qty: minQty,
                maxQty,
                label: makeQtyRangeLabel(minQty, maxQty),
                savingsPercent,
            };
        });
    }, [activeBreakpointLookup, activeUnitConversions, activeBaseUom, selectedUom]);

    const activeBreakpointIndex = useMemo(() => {
        if (breakpointRows.length === 0) return -1;

        let idx = 0;
        for (let i = 0; i < breakpointRows.length; i++) {
            if (selectedQuantity >= breakpointRows[i].min_qty) {
                idx = i;
                continue;
            }
            break;
        }

        return idx;
    }, [breakpointRows, selectedQuantity]);

    useEffect(() => {
        const inventorySelect = document.getElementById('weka-inventory-select') as HTMLSelectElement | null;
        if (!inventorySelect) return;

        setSelectedWarehouse(String(inventorySelect.value || props.selectedWarehouseId || '').trim());
        const handleWarehouseChange = () => setSelectedWarehouse(inventorySelect.value);
        inventorySelect.addEventListener('change', handleWarehouseChange);
        return () => inventorySelect.removeEventListener('change', handleWarehouseChange);
    }, [props.selectedWarehouseId]);

    const policyStepQty = getPolicyStep(quantityPolicy);
    const policyMinNonZeroQty = getPolicyMinNonZero(quantityPolicy);
    const policyMaxQty = getPolicyMax(quantityPolicy);
    const allowZeroSelection = quantityPolicy.allow_zero === true;
    const decimalAllowed = quantityPolicy.is_decimal_allowed === true;
    const policyPrecision = getPolicyPrecision(quantityPolicy);
    const policyCanPurchase = quantityPolicy.can_purchase !== false;
    const selectedUomDisplay = UOM_LABELS[selectedUom] || selectedUom;
    const serialQuantityLocked = activeSerialEnabled && activeSerialForceBuyAll && selectedSerialOption !== null;
    const backInStockEnabled = normalizeOptionalBoolean(props.backInStockEnabled) === true;
    const showBackorderLabelOnAddToCart = showStock && normalizeOptionalBoolean(props.showBackorderLabelOnAddToCart) === true;
    const backInStockButtonLabel = String(props.backInStockButtonLabel || '').trim() || 'Notify when back in stock';
    const backInStockSuccessMessage = String(props.backInStockSuccessMessage || '').trim() || 'We will email you when this item is back in stock.';
    const backInStockRemoveButtonLabel = 'Remove back in stock notification';
    const backInStockRemoveMessage = 'Back in stock notification removed.';
    const backInStockTargetKey = useMemo(
        () => buildBackInStockTargetKey(props.product.id, selectedVariationId),
        [props.product.id, selectedVariationId]
    );
    const isCurrentBackInStockSubscribed = backInStockTargetKey !== '' && subscribedBackInStockTargets.has(backInStockTargetKey);
    const showBackInStockButton = useMemo(() => {
        return canShowCompactElement('backInStockButton') && showStock && shouldOfferBackInStock({
            featureEnabled: backInStockEnabled,
            isVariableProduct,
            isVariationSelectionComplete,
            selectedVariation,
            selectedVariationId,
            activeIsPurchasable,
            activeInventoryPolicy: quantityPolicy,
        });
    }, [
        isCompactMode,
        compactElementVisibility.backInStockButton,
        showStock,
        backInStockEnabled,
        isVariableProduct,
        isVariationSelectionComplete,
        selectedVariation,
        selectedVariationId,
        activeIsPurchasable,
        quantityPolicy,
    ]);

    useEffect(() => {
        if (!serialQuantityLocked || !selectedSerialOption) {
            return;
        }

        const forcedQuantity = Number(selectedSerialOption.quantity || 0);
        const safeForcedQuantity = Number.isFinite(forcedQuantity) ? Math.max(0, forcedQuantity) : 0;
        setSelectedQuantity(safeForcedQuantity);
        setQuantityNotice('');
    }, [serialQuantityLocked, selectedSerialOption]);

    useEffect(() => {
        setBackInStockNotice('');
    }, [props.product.id, backInStockTargetKey]);

    useEffect(() => {
        setSubscribedBackInStockTargets(new Set(resolveBackInStockTargetKeys(props.initialBackInStockTargetKeys)));
    }, [props.product.id, props.initialBackInStockTargetKeys]);

    useEffect(() => {
        return onBackInStockTargetStateChange(() => {
            setSubscribedBackInStockTargets(new Set(resolveBackInStockTargetKeys(props.initialBackInStockTargetKeys)));
        });
    }, [props.product.id, props.initialBackInStockTargetKeys]);

    const applyQuantityWithPolicy = (nextQuantity: number, showNotice: boolean) => {
        if (serialQuantityLocked && selectedSerialOption) {
            const forcedQuantity = Number(selectedSerialOption.quantity || 0);
            const safeForcedQuantity = Number.isFinite(forcedQuantity) ? Math.max(0, forcedQuantity) : 0;
            setSelectedQuantity(safeForcedQuantity);
            if (showNotice) {
                setQuantityNotice(`Quantity locked to selected serial stock (${formatQuantity(safeForcedQuantity, true, policyPrecision)}).`);
            }
            return;
        }

        const result = clampQuantityToPolicy(nextQuantity, quantityPolicy);
        setSelectedQuantity(result.quantity);

        if (showNotice && result.adjusted) {
            setQuantityNotice(buildPolicyMessage(result, quantityPolicy, selectedUomDisplay, showStock));
        } else if (!result.adjusted) {
            setQuantityNotice('');
        }
    };

    const minusButtonClicked = () => {
        if (serialQuantityLocked) {
            return;
        }

        const decrement = policyStepQty;
        if (selectedQuantity <= 0) {
            return;
        }

        let nextQuantity = selectedQuantity - decrement;
        if (allowZeroSelection) {
            if (nextQuantity < policyMinNonZeroQty) {
                nextQuantity = 0;
            }
        } else if (nextQuantity < policyMinNonZeroQty) {
            nextQuantity = policyMinNonZeroQty;
        }

        applyQuantityWithPolicy(nextQuantity, false);
    };

    const plusButtonClicked = () => {
        if (serialQuantityLocked) {
            return;
        }

        if (!policyCanPurchase) {
            return;
        }

        let nextQuantity = selectedQuantity + policyStepQty;
        if (allowZeroSelection && selectedQuantity <= 0) {
            nextQuantity = policyMinNonZeroQty;
        }

        applyQuantityWithPolicy(nextQuantity, false);
    };

    const onQuantityChange = (evt: ChangeEvent<HTMLInputElement>) => {
        if (serialQuantityLocked) {
            return;
        }

        const rawValue = String(evt.target.value || '').trim();
        if (rawValue === '') {
            setSelectedQuantity(0);
            setQuantityNotice('');
            return;
        }

        const normalizedValue = rawValue.replace(/,/g, '');
        const parsedQuantity = Number(normalizedValue);
        if (!Number.isFinite(parsedQuantity)) {
            return;
        }

        applyQuantityWithPolicy(parsedQuantity, true);
    };

    const onBreakpointSelected = (minQty: number) => {
        applyQuantityWithPolicy(minQty, true);
        if (isCompactMode) {
            setCompactBreaksOpen(false);
        }
    };

    const totalPrice = pricing(selectedUom, selectedQuantity);
    const unitPriceReferenceQuantity = selectedQuantity > 0
        ? selectedQuantity
        : Math.max(1, policyMinNonZeroQty);
    const unitPriceReferenceTotal = pricing(selectedUom, unitPriceReferenceQuantity);
    const unitPrice = unitPriceReferenceQuantity > 0
        ? (unitPriceReferenceTotal / unitPriceReferenceQuantity)
        : 0;
    const activeComparePrice = useMemo(() => {
        if (activeComparePriceSource === COMPARE_PRICE_SOURCE_PRICING_TABLE_QTY_1) {
            return findFirstBreakpointComparePrice(
                activeBreakpointLookup,
                selectedUom,
                activeUnitConversions,
                activeBaseUom
            );
        }

        if (
            activeComparePriceSource === COMPARE_PRICE_SOURCE_PRODUCT_META
            || activeComparePriceSource === COMPARE_PRICE_SOURCE_REGULAR
        ) {
            return convertComparePriceForUom(
                activeRawComparePrice,
                activeUnitConversions,
                selectedUom,
                activeBaseUom
            );
        }

        return convertComparePriceForUom(
            activeRawComparePrice,
            activeUnitConversions,
            selectedUom,
            activeBaseUom
        );
    }, [
        activeComparePriceSource,
        activeBreakpointLookup,
        activeRawComparePrice,
        activeUnitConversions,
        selectedUom,
        activeBaseUom,
    ]);
    const showComparePrice = activeComparePrice > unitPrice + 0.000001;

    const showUomTabs = activeUomEnabled && resolvedUoms.length > 1 && !isCompactMode;
    const showCompactUomSelect = activeUomEnabled
        && resolvedUoms.length > 1
        && isCompactMode
        && !usesCompactDetailsModal
        && canShowCompactElement('uomSelector');
    const shouldRenderQuantityBreakTable = quantityBreaksEnabled && showQuantityBreaksTableSetting && breakpointRows.length > 1;
    const shouldUseCompactBreakToggle = isCompactMode && !usesCompactDetailsModal && shouldRenderQuantityBreakTable;
    const shouldShowBreakTableRows = shouldRenderQuantityBreakTable && (!shouldUseCompactBreakToggle || compactBreaksOpen);
    const showSerialSelector = activeSerialEnabled && canShowCompactElement('serialSelector');
    const showPurchaseOptionBlock = !usesCompactDetailsModal
        && (showVariationSelectors || showUomTabs || shouldRenderQuantityBreakTable || showCompactUomSelect || showSerialSelector);
    const canShowQuoteButton = (props.showQuoteButton === true || props.showQuoteButton === 'true')
        && canShowCompactElement('quoteButton')
        && (
            props.canCreateQuotes === true
            || props.canCreateQuotes === 'true'
            || props.canEditQuotes === true
            || props.canEditQuotes === 'true'
        );
    const canShowDraftOrderButton = (
        (props.draftOrdersEnabled === true || props.draftOrdersEnabled === 'true')
        && canShowCompactElement('draftButton')
        && (
            props.canCreateDraftOrders === true
            || props.canCreateDraftOrders === 'true'
            || props.canEditDraftItems === true
            || props.canEditDraftItems === 'true'
        )
    );
    const showEnquiryButton = isEnquiryProduct && allowAddToCart && canShowCompactElement('addToCartButton');
    const canShowAddToCartButton = !showEnquiryButton && allowAddToCart && canShowCompactElement('addToCartButton');
    const showQuantityControls = canShowCompactElement('quantityControls')
        && !showEnquiryButton
        && (
            isCompactCartDetailsMode
                ? canShowAddToCartButton
                : (canShowAddToCartButton || canShowDraftOrderButton || canShowQuoteButton)
        );
    const selectedCurrencyCode = useMemo(() => resolvePricingCurrencyCode(
        activeBreakpointLookup,
        activeSalesPrices,
        selectedUom,
        quantityBreaksEnabled,
        selectedQuantity,
        String(props.defaultCurrencyCode || '')
    ), [activeBreakpointLookup, activeSalesPrices, selectedUom, quantityBreaksEnabled, selectedQuantity, props.defaultCurrencyCode]);
    const selectedCurrencySymbol = useMemo(() => resolvePricingCurrencySymbol(
        activeBreakpointLookup,
        activeSalesPrices,
        selectedUom,
        quantityBreaksEnabled,
        selectedQuantity,
        String(props.defaultCurrencySymbol || '')
    ), [activeBreakpointLookup, activeSalesPrices, selectedUom, quantityBreaksEnabled, selectedQuantity, props.defaultCurrencySymbol]);
    const compactStartingPrice = useMemo<CompactStartingPriceSummary | null>(() => {
        if (!effectiveShowPrice) {
            return null;
        }

        if (!isVariableProduct) {
            if (!Number.isFinite(unitPrice) || unitPrice < 0) {
                return null;
            }

            return {
                amount: unitPrice,
                currencyCode: selectedCurrencyCode,
                currencySymbol: selectedCurrencySymbol,
            };
        }

        let bestPrice: CompactStartingPriceSummary | null = null;

        normalizedAvailableVariations.forEach((variation) => {
            if (variation.isPurchasable === false || variation.isInStock === false) {
                return;
            }

            if (String(variation.pricingUnavailableMessage || '').trim() !== '') {
                return;
            }

            const variationBaseUom = String(variation.baseUom || 'EA').trim().toUpperCase() || 'EA';
            const variationUnitConversions = Array.isArray(variation.unitConversions) ? variation.unitConversions : [];
            const variationSalesPrices = variation.salesPrices ?? {pricing: {}, quantity_breaks: {}};
            const variationBreakpointLookup = variation.breakpointLookup;
            const variationUomEnabled = variation.uomEnabled === true;
            const variationAvailableUoms = variationUomEnabled
                ? (Array.isArray(variation.availableUoms) && variation.availableUoms.length > 0
                    ? variation.availableUoms
                    : [variationBaseUom])
                : ['EA'];
            const variationDefaultUom = getDefaultUom(
                variationUomEnabled,
                variationAvailableUoms,
                variationBreakpointLookup,
                variationBaseUom
            );
            const variationPolicy = resolveInventoryPolicyForUom(
                variation.inventoryPolicy,
                variation.inventoryPolicyByUom,
                variationDefaultUom
            );
            const variationQuantity = Math.max(1, getInitialQuantityForPolicy(variationPolicy));
            const fallbackBasePrice = Number(props.basePrice || 0);
            const variationBasePrice = Number(variation.basePrice ?? variation.price ?? fallbackBasePrice);
            const pricingCalculator = createPricingCalculator(
                Number.isFinite(variationBasePrice) ? variationBasePrice : fallbackBasePrice,
                variationUnitConversions,
                variationSalesPrices,
                variationBreakpointLookup,
                quantityBreaksEnabled,
                variationBaseUom
            );
            const variationTotal = pricingCalculator(variationDefaultUom, variationQuantity);
            const variationUnitPrice = variationQuantity > 0 ? (variationTotal / variationQuantity) : 0;

            if (!Number.isFinite(variationUnitPrice) || variationUnitPrice < 0) {
                return;
            }

            const candidate: CompactStartingPriceSummary = {
                amount: variationUnitPrice,
                currencyCode: resolvePricingCurrencyCode(
                    variationBreakpointLookup,
                    variationSalesPrices,
                    variationDefaultUom,
                    quantityBreaksEnabled,
                    variationQuantity,
                    String(props.defaultCurrencyCode || '')
                ),
                currencySymbol: resolvePricingCurrencySymbol(
                    variationBreakpointLookup,
                    variationSalesPrices,
                    variationDefaultUom,
                    quantityBreaksEnabled,
                    variationQuantity,
                    String(props.defaultCurrencySymbol || '')
                ),
            };

            if (!bestPrice || candidate.amount < bestPrice.amount) {
                bestPrice = candidate;
            }
        });

        return bestPrice;
    }, [
        effectiveShowPrice,
        isVariableProduct,
        unitPrice,
        selectedCurrencyCode,
        selectedCurrencySymbol,
        normalizedAvailableVariations,
        props.basePrice,
        props.defaultCurrencyCode,
        props.defaultCurrencySymbol,
        quantityBreaksEnabled,
    ]);
    const compactTotalPrice = useMemo(
        () => formatMoneyPartsWithCurrencyCode(toDisplayPrice(totalPrice), selectedCurrencyCode, currencyDisplayMode, selectedCurrencySymbol),
        [totalPrice, selectedCurrencyCode, currencyDisplayMode, selectedCurrencySymbol, priceDisplayMultiplier]
    );
    const compactTotalPriceSuffix = buildDisplayPriceSuffix(totalPrice, selectedCurrencyCode, selectedCurrencySymbol);
    const showCompactItemPrice = canShowCompactElement('itemPrice') && effectiveShowPrice;
    const showCompactCalculatedPrice = canShowCompactElement('calculatedPrice') && effectiveShowPrice && !props.hideCalculatedPrice;
    const showInlineCompactItemPrice = showCompactItemPrice && !usesCompactDetailsModal;
    const showInlineCompactCalculatedPrice = showCompactCalculatedPrice && !usesCompactDetailsModal;
    const shouldShowCompactPriceSummary = isCompactMode
        && !usesCompactDetailsModal
        && (showInlineCompactItemPrice || showInlineCompactCalculatedPrice);

    const quantityInputStep = decimalAllowed
        ? String(normalizeQuantity(policyStepQty, true, policyPrecision))
        : String(Math.max(1, Math.round(policyStepQty)));
    const quantityInputValue = formatQuantity(selectedQuantity, decimalAllowed, policyPrecision);
    const canManageLineItem = policyCanPurchase
        && activeIsPurchasable
        && !showBackInStockButton
        && isVariationSelectionValid;
    const canAdjustQuantity = showQuantityControls && canManageLineItem;
    const canIncreaseQty = !serialQuantityLocked
        && canAdjustQuantity
        && (policyMaxQty === null || selectedQuantity + 0.000001 < policyMaxQty || selectedQuantity <= 0);
    const canDecreaseQty = !serialQuantityLocked && canAdjustQuantity && selectedQuantity > 0;
    const canBuildSavedItem = canManageLineItem
        && selectedQuantity > 0
        && (policyMaxQty === null || selectedQuantity <= policyMaxQty + 0.000001);
    const canAddToCart = canShowAddToCartButton && canBuildSavedItem;
    const canTriggerAddToCart = canAddToCart && !isAddingToCart;
    const shouldRenderQuantityControls = !isCompactDetailsMode && showQuantityControls;
    const shouldRenderQuantityActions = !isCompactDetailsMode && (shouldRenderQuantityControls || showBackInStockButton || showEnquiryButton);
    const detailsModalProduct = detailsModalResponse.results[0] || null;
    const openDetailsModal = () => {
        setDetailsModalError('');
        setIsDetailsModalOpen(true);
    };
    const closeDetailsModal = () => {
        setIsDetailsModalOpen(false);
        if (typeof window !== 'undefined') {
            window.requestAnimationFrame(() => {
                detailsButtonRef.current?.focus();
            });
            return;
        }

        detailsButtonRef.current?.focus();
    };
    const shouldUseBackorderButtonLabel = showBackorderLabelOnAddToCart
        && quantityPolicy.backorders_require_notification === true
        && (
            String(quantityPolicy.normalized_status || '').trim().toLowerCase() === 'backorder'
            || quantityPolicy.is_on_backorder === true
        );
    const addToCartButtonLabel = isAddingToCart
        ? 'Adding...'
        : (!isVariationSelectionValid
            ? 'Select options'
            : (shouldUseBackorderButtonLabel
                ? 'Backorder'
                : (isCompactMode
                    ? 'Add to Cart'
                : `Add to Cart${effectiveShowPrice && !props.hideCalculatedPrice ? ` - ${formatDisplayMoney(totalPrice, selectedCurrencyCode, selectedCurrencySymbol)}` : ''}`)));

    const quantityHint = useMemo(() => {
        const hints: string[] = [];

        if (variationSelectionMessage !== '') {
            hints.push(variationSelectionMessage);
        }

        if (quantityPolicy.step_mode_enabled === true) {
            hints.push(`MOQ increments: 0, ${formatQuantity(policyStepQty, decimalAllowed, policyPrecision)}, ${formatQuantity(policyStepQty * 2, decimalAllowed, policyPrecision)}...`);
        } else if (policyStepQty > 1) {
            hints.push(`Step: ${formatQuantity(policyStepQty, decimalAllowed, policyPrecision)}`);
        }

        if (showStock && policyMaxQty !== null) {
            if (policyMaxQty <= 0.000001 && quantityPolicy.stock_fully_reserved_in_cart === true) {
                hints.push('No additional quantity available (already in cart)');
            } else {
                hints.push(`Max: ${formatQuantity(policyMaxQty, decimalAllowed, policyPrecision)} ${selectedUomDisplay}`);
            }
        }

        if (showStock && quantityPolicy.status_label) {
            hints.push(`Status: ${quantityPolicy.status_label}`);
        }

        if (showBackInStockButton) {
            hints.push('Currently out of stock');
        }

        if (showStock && activeSerialEnabled && !serialLoading && serialOptions.length === 0 && (!isVariableProduct || selectedVariationId > 0)) {
            hints.push('No serial numbers available');
        }

        if (showStock && serialQuantityLocked && selectedSerialOption) {
            hints.push(`Serial lock qty: ${formatQuantity(selectedSerialOption.quantity, true, policyPrecision)}`);
        }

        return hints.join(' | ');
    }, [
        quantityPolicy.step_mode_enabled,
        quantityPolicy.status_label,
        quantityPolicy.stock_fully_reserved_in_cart,
        decimalAllowed,
        policyMaxQty,
        policyPrecision,
        policyStepQty,
        selectedUomDisplay,
        variationSelectionMessage,
        activeSerialEnabled,
        serialLoading,
        serialOptions.length,
        serialQuantityLocked,
        selectedSerialOption,
        showBackInStockButton,
        showStock,
        isVariableProduct,
        selectedVariationId,
    ]);

    const addToCartApiEndpoint = useMemo(() => buildApiEndpoint('cart/add-item'), []);
    const backInStockApiEndpoint = useMemo(() => buildApiEndpoint('back-in-stock/subscriptions'), []);

    const buildSavedItemPayload = (destinationLabel: string): DraftOrderItemInput | null => {
        if (!canBuildSavedItem) {
            return null;
        }

        if (isVariableProduct && (!isVariationSelectionComplete || selectedVariationId <= 0)) {
            setQuantityNotice(`Please choose product options before adding to ${destinationLabel}.`);
            return null;
        }

        let effectiveQuantity = selectedQuantity;
        if (selectedSerialOption && activeSerialForceBuyAll) {
            const forcedQuantity = Number(selectedSerialOption.quantity || 0);
            if (Number.isFinite(forcedQuantity) && forcedQuantity > 0) {
                effectiveQuantity = forcedQuantity;
            }
        }

        if (!effectiveQuantity || effectiveQuantity <= 0) {
            setQuantityNotice(`Please choose a valid quantity before adding to ${destinationLabel}.`);
            return null;
        }

        const payload: DraftOrderItemInput = {
            product_id: props.product.id,
            quantity: effectiveQuantity,
        };

        if (activeUomEnabled && selectedUom !== '') {
            payload.uom = selectedUom;
        }
        if (selectedWarehouse !== '') {
            payload.warehouse_id = selectedWarehouse;
        }
        if (selectedSerialOption) {
            payload.serial_number = selectedSerialOption.serial_number;
            payload.serial_warehouse_id = selectedSerialOption.warehouse_id || '';
            payload.serial_location_id = selectedSerialOption.location_id || '';
        }
        if (selectedVariationId > 0) {
            payload.variation_id = selectedVariationId;
            payload.variation = selectedVariationPayload;
        }

        return payload;
    };

    const buildDraftItemPayload = (): DraftOrderItemInput | null => buildSavedItemPayload('a draft order');

    const buildQuoteItemPayload = (): QuoteItemInput | null => {
        const payload = buildSavedItemPayload('a quote');
        if (!payload) {
            return null;
        }

        return {
            ...payload,
            price: Number.isFinite(unitPrice) ? unitPrice : 0,
            discount: 0,
        };
    };

    const resetQuantityAfterSuccess = () => {
        if (activeSerialForceBuyAll && selectedSerialOption) {
            setSelectedSerialKey('');
        }

        setSelectedQuantity(0);
    };

    const addToCartAjax = async (): Promise<boolean> => {
        if (!canAddToCart || isAddingToCart || addToCartInFlightRef.current) {
            return false;
        }

        if (typeof window === 'undefined') {
            return false;
        }

        if (isVariableProduct && (!isVariationSelectionComplete || selectedVariationId <= 0)) {
            const message = 'Please choose product options before adding to cart.';
            toast.error(message);
            return false;
        }

        const nonce = String((window as any)?.wekaApiSettings?.nonce || (window as any)?.wpApiSettings?.nonce || '');
        const payload: Record<string, unknown> = {
            product_id: props.product.id,
            quantity: selectedQuantity,
            surface: String(props.surfaceId || 'product_add_to_cart'),
        };
        if (activeUomEnabled && selectedUom !== '') {
            payload.uom = selectedUom;
        }
        if (selectedWarehouse !== '') {
            payload.warehouse_id = selectedWarehouse;
        }
        if (selectedSerialOption) {
            payload.serial_number = selectedSerialOption.serial_number;
            payload.serial_warehouse_id = selectedSerialOption.warehouse_id || '';
            payload.serial_location_id = selectedSerialOption.location_id || '';
        }
        if (selectedVariationId > 0) {
            payload.variation_id = selectedVariationId;
            payload.variation = selectedVariationPayload;
            Object.entries(selectedVariationPayload).forEach(([attributeKey, attributeValue]) => {
                payload[attributeKey] = attributeValue;
            });
        }

        addToCartInFlightRef.current = true;
        setIsAddingToCart(true);
        setWoocommerceNoticesHtml('');
        try {
            const response = await fetch(addToCartApiEndpoint, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify(payload),
            });

            let data: any = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            const noticesHtml = normalizeNoticesHtml(data?.notices_html ?? data?.data?.notices_html);
            if (!response.ok || data?.success !== true) {
                setWoocommerceNoticesHtml(noticesHtml);
                const errorMessage = String(data?.message || data?.error || 'Failed to add product to cart.');
                throw new Error(errorMessage);
            }

            setWoocommerceNoticesHtml(noticesHtml);

            applyWooCartUpdates(data);

            const successMessage = `Added ${formatQuantity(selectedQuantity, decimalAllowed, policyPrecision)} ${selectedUomDisplay} to cart.`;
            resetQuantityAfterSuccess();
            toast.success(successMessage);

            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to add product to cart.';
            toast.error(message);
            return false;
        } finally {
            setIsAddingToCart(false);
            addToCartInFlightRef.current = false;
        }
    };

    const subscribeToBackInStock = async (): Promise<boolean> => {
        if (!showBackInStockButton || isBackInStockSubmitting || backInStockTargetKey === '') {
            return false;
        }

        if (typeof window === 'undefined') {
            return false;
        }

        if (isVariableProduct && (!isVariationSelectionComplete || selectedVariationId <= 0)) {
            setBackInStockNotice('Please choose product options before tracking stock.');
            return false;
        }

        const nonce = String((window as any)?.wekaApiSettings?.nonce || (window as any)?.wpApiSettings?.nonce || '');
        const payload: Record<string, unknown> = {
            product_id: props.product.id,
            source_surface: 'add_to_cart_widget',
        };

        if (selectedVariationId > 0) {
            payload.variation_id = selectedVariationId;
            payload.variation = selectedVariationPayload;
        }

        setIsBackInStockSubmitting(true);
        setWoocommerceNoticesHtml('');

        try {
            const response = await fetch(backInStockApiEndpoint, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify(payload),
            });

            let data: any = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            if (!response.ok || data?.success !== true) {
                const errorMessage = String(data?.message || data?.error || 'Failed to save the stock reminder.');
                throw new Error(errorMessage);
            }

            const responseMessage = String(data?.message || '').trim() || backInStockSuccessMessage;
            setSubscribedBackInStockTargets((previousTargets) => {
                const nextTargets = new Set(previousTargets);
                nextTargets.add(backInStockTargetKey);
                return nextTargets;
            });
            setBackInStockTargetSubscriptionState(backInStockTargetKey, true);
            setBackInStockNotice(responseMessage);
            setQuantityNotice('');

            return true;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to save the stock reminder.';
            setBackInStockNotice(errorMessage);
            return false;
        } finally {
            setIsBackInStockSubmitting(false);
        }
    };

    const removeBackInStockSubscription = async (): Promise<boolean> => {
        if (!showBackInStockButton || isBackInStockSubmitting || backInStockTargetKey === '') {
            return false;
        }

        if (typeof window === 'undefined') {
            return false;
        }

        const nonce = String((window as any)?.wekaApiSettings?.nonce || (window as any)?.wpApiSettings?.nonce || '');
        const payload: Record<string, unknown> = {
            product_id: props.product.id,
        };

        if (selectedVariationId > 0) {
            payload.variation_id = selectedVariationId;
        }

        setIsBackInStockSubmitting(true);
        setWoocommerceNoticesHtml('');

        try {
            const response = await fetch(backInStockApiEndpoint, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify(payload),
            });

            let data: any = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            if (!response.ok || data?.success !== true) {
                const errorMessage = String(data?.message || data?.error || 'Failed to remove the stock reminder.');
                throw new Error(errorMessage);
            }

            const responseMessage = String(data?.message || '').trim() || backInStockRemoveMessage;
            setSubscribedBackInStockTargets((previousTargets) => {
                const nextTargets = new Set(previousTargets);
                nextTargets.delete(backInStockTargetKey);
                return nextTargets;
            });
            setBackInStockTargetSubscriptionState(backInStockTargetKey, false);
            setBackInStockNotice(responseMessage);
            setQuantityNotice('');

            return true;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to remove the stock reminder.';
            setBackInStockNotice(errorMessage);
            return false;
        } finally {
            setIsBackInStockSubmitting(false);
        }
    };

    return (
        <div className={`weka-add-to-cart ${isCompactMode ? 'weka-add-to-cart--compact' : 'weka-add-to-cart--full'}`}>
            {woocommerceNoticesHtml !== '' && (
                <div
                    className={'woocommerce-notices-wrapper weka-add-to-cart-notices'}
                    dangerouslySetInnerHTML={{__html: woocommerceNoticesHtml}}
                />
            )}
            {(activeIsPurchasable || showEnquiryButton || isVariableProduct || usesCompactDetailsModal) && (
                <>
                    {isCompactMode && canShowCompactElement('productSku') && activeProductSku !== '' && (
                        <div className={'weka-compact-product-sku'}>
                            <span className={'weka-compact-product-sku__label'}>SKU</span>
                            <span className={'weka-compact-product-sku__value'}>{activeProductSku}</span>
                        </div>
                    )}
                    {usesCompactDetailsModal && (
                        (canShowCompactElement('itemPrice') && compactStartingPrice !== null)
                        || (canShowCompactElement('messages') && activePricingUnavailableMessage !== '')
                        || canShowCompactElement('detailsButton')
                    ) && (
                        <div className={'weka-compact-details-summary'}>
                            {canShowCompactElement('itemPrice') && compactStartingPrice ? (
                                <div className={'weka-compact-details-summary__price'}>
                                    <span className={'weka-compact-details-summary__label'}>From</span>
                                    <strong className={'weka-compact-details-summary__amount'}>
                                        {formatDisplayMoney(
                                            compactStartingPrice.amount,
                                            compactStartingPrice.currencyCode,
                                            compactStartingPrice.currencySymbol,
                                        )}
                                    </strong>
                                </div>
                            ) : canShowCompactElement('messages') && activePricingUnavailableMessage !== '' ? (
                                <p className={'weka-compact-details-summary__message text-danger'}>
                                    {activePricingUnavailableMessage}
                                </p>
                            ) : null}
                            {canShowCompactElement('detailsButton') && (
                                <button
                                    ref={detailsButtonRef}
                                    type={'button'}
                                    className={'button weka-secondary-btn button-small weka-compact-details-summary__button'}
                                    onClick={openDetailsModal}
                                >
                                    {compactDetailsButtonLabel}
                                </button>
                            )}
                        </div>
                    )}
                    {showPurchaseOptionBlock && (
                        <div className={`purchase-option-block ${isCompactMode ? 'purchase-option-block--compact' : ''}`}>
                            {showVariationSelectors && (
                                <div className={'variation-select-group'}>
                                    <div className={'purchase-option-header'}>
                                        <span className={'purchase-option-title'}>Product Options</span>
                                    </div>
                                    <div className={`variation-select-grid ${isCompactMode ? 'variation-select-grid--compact' : ''}`}>
                                        {normalizedVariationAttributes.map((attribute) => {
                                            const normalizedKey = String(attribute.key || '').replace(/[^a-z0-9_-]/gi, '-');
                                            const fieldId = `weka-variation-${props.product.id}-${normalizedKey}`;
                                            const selectedValue = normalizeVariationValue(selectedVariationAttributes[attribute.key]);
                                            const attributeLabel = String(attribute.label || attribute.name || attribute.key).trim();
                                            const optionPromptLabel = attributeLabel !== '' ? attributeLabel : 'option';

                                            return (
                                                <div className={'variation-select-row'} key={attribute.key}>
                                                    <label htmlFor={fieldId} className={'variation-select-label'}>
                                                        {attributeLabel || attribute.key.replace(/^attribute_/, '')}
                                                    </label>
                                                    <select
                                                        id={fieldId}
                                                        className={'variation-select'}
                                                        value={selectedValue}
                                                        onChange={(evt) => {
                                                            const nextValue = normalizeVariationValue(evt.currentTarget.value);
                                                            setSelectedVariationAttributes((previousSelection) => ({
                                                                ...previousSelection,
                                                                [attribute.key]: nextValue,
                                                            }));
                                                            setSelectedSerialKey('');
                                                            setQuantityNotice('');
                                                        }}
                                                    >
                                                        <option value="">{`Choose ${optionPromptLabel}`}</option>
                                                        {attribute.options.map((option) => {
                                                            const optionValue = normalizeVariationValue(option.value);
                                                            const optionSelectable = isVariationOptionSelectable(attribute.key, optionValue);
                                                            return (
                                                                <option
                                                                    key={`${attribute.key}-${optionValue}`}
                                                                    value={optionValue}
                                                                    disabled={!optionSelectable}
                                                                >
                                                                    {option.label || optionValue}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {showUomTabs && (
                                <>
                                    <div className={'purchase-option-header'}>
                                        <span className={'purchase-option-title'}>Packaging Unit (UOM)</span>
                                    </div>
                                    <div className={'uom-pill-group'} role={'tablist'} aria-label={'Select packaging unit'}>
                                        {resolvedUoms.map((uom) => {
                                            const isActive = selectedUom === uom;
                                            return (
                                                <button
                                                    key={uom}
                                                    type={'button'}
                                                    className={`uom-pill ${isActive ? 'is-active' : ''}`}
                                                    onClick={() => {
                                                        setSelectedUom(uom);
                                                        setQuantityNotice('');
                                                    }}
                                                >
                                                    {UOM_LABELS[uom] || uom}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {showCompactUomSelect && (
                                <div className={'uom-compact-wrap'}>
                                    <label htmlFor={`weka-uom-${props.product.id}`} className={'uom-compact-label'}>UOM</label>
                                    <select
                                        id={`weka-uom-${props.product.id}`}
                                        value={selectedUom}
                                        className={'uom-select uom-select-compact'}
                                        onChange={(evt) => {
                                            setSelectedUom(evt.currentTarget.value);
                                            setQuantityNotice('');
                                        }}
                                    >
                                        {resolvedUoms.map((uom) => (
                                            <option value={uom} key={uom}>{UOM_LABELS[uom] || uom}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {!showCompactUomSelect && (
                                <select
                                    value={selectedUom}
                                    className={'uom-select hidden-uom-select'}
                                    onChange={(evt) => {
                                        setSelectedUom(evt.currentTarget.value);
                                        setQuantityNotice('');
                                    }}
                                    aria-hidden={'true'}
                                    tabIndex={-1}
                                >
                                    {resolvedUoms.map((uom) => (
                                        <option value={uom} key={uom}>{uom}</option>
                                    ))}
                                </select>
                            )}

                            {showSerialSelector && shouldRenderQuantityControls && (
                                <div className={'serial-select-wrap'}>
                                    <label htmlFor={`weka-serial-${props.product.id}`} className={'uom-compact-label'}>
                                        Serial Number
                                    </label>
                                    {activeSerialDisplayMode === 'table' ? (
                                        <div style={{maxHeight: '220px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px'}}>
                                            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12px'}}>
                                                <thead>
                                                    <tr>
                                                        <th style={{textAlign: 'left', padding: '6px'}}>Use</th>
                                                        <th style={{textAlign: 'left', padding: '6px'}}>Serial</th>
                                                        <th style={{textAlign: 'left', padding: '6px'}}>Warehouse</th>
                                                        {showStock && <th style={{textAlign: 'left', padding: '6px'}}>Qty</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td style={{padding: '6px'}}>
                                                            <input
                                                                type="radio"
                                                                name={`weka-serial-table-${props.product.id}`}
                                                                value=""
                                                                checked={selectedSerialKey === ''}
                                                                onChange={() => {
                                                                    setSelectedSerialKey('');
                                                                    setQuantityNotice('');
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={{padding: '6px'}} colSpan={showStock ? 3 : 2}>{showStock ? 'Use total stock (no specific serial)' : 'No specific serial'}</td>
                                                    </tr>
                                                    {serialOptions.map((option) => {
                                                        const optionKey = getSerialOptionKey(option);
                                                        return (
                                                            <tr key={optionKey}>
                                                                <td style={{padding: '6px'}}>
                                                                    <input
                                                                        type="radio"
                                                                        name={`weka-serial-table-${props.product.id}`}
                                                                        value={optionKey}
                                                                        checked={selectedSerialKey === optionKey}
                                                                        onChange={() => {
                                                                            setSelectedSerialKey(optionKey);
                                                                            setQuantityNotice('');
                                                                        }}
                                                                        disabled={serialLoading}
                                                                    />
                                                                </td>
                                                                <td style={{padding: '6px'}}>{option.serial_number}</td>
                                                                <td style={{padding: '6px'}}>{option.warehouse_id || '-'}</td>
                                                                {showStock && <td style={{padding: '6px'}}>{formatQuantity(option.quantity, true, policyPrecision)}</td>}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <select
                                            id={`weka-serial-${props.product.id}`}
                                            value={selectedSerialKey}
                                            className={'uom-select serial-select'}
                                            onChange={(evt) => {
                                                const nextKey = evt.currentTarget.value;
                                                setSelectedSerialKey(nextKey);
                                                setQuantityNotice('');
                                            }}
                                            disabled={serialLoading || serialOptions.length === 0}
                                        >
                                            <option value="">{showStock ? 'Use total stock (no serial)' : 'No specific serial'}</option>
                                            {serialOptions.map((option) => {
                                                const optionKey = getSerialOptionKey(option);
                                                const label = option.label && option.label !== ''
                                                    ? option.label
                                                    : (showStock
                                                        ? `${option.serial_number} | Qty: ${formatQuantity(option.quantity, true, policyPrecision)}`
                                                        : option.serial_number);
                                                return (
                                                    <option key={optionKey} value={optionKey}>
                                                        {label}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    )}
                                    {serialLoading && (
                                        <small className={'unit-price-helper'}>Loading serial options...</small>
                                    )}
                                    {!serialLoading && serialError !== '' && (
                                        <small className={'unit-price-helper'}>{serialError}</small>
                                    )}
                                    {!serialLoading && serialError === '' && serialOptions.length === 0 && (
                                        <small className={'unit-price-helper'}>No serial numbers available for this product.</small>
                                    )}
                                    {serialQuantityLocked && selectedSerialOption && (
                                        <small className={'unit-price-helper'}>
                                            {showStock
                                                ? `Force buy all enabled. Quantity locked to ${formatQuantity(selectedSerialOption.quantity, true, policyPrecision)}.`
                                                : 'Force buy all enabled for the selected serial.'}
                                        </small>
                                    )}
                                </div>
                            )}

                            {shouldRenderQuantityBreakTable && (
                                <div className={`quantity-break-table ${isCompactMode ? 'quantity-break-table--compact' : ''}`}>
                                    {shouldUseCompactBreakToggle && (
                                        <button
                                            type={'button'}
                                            className={`quantity-break-toggle ${compactBreaksOpen ? 'is-open' : ''}`}
                                            aria-expanded={compactBreaksOpen}
                                            onClick={() => setCompactBreaksOpen((prev) => !prev)}
                                        >
                                            <span>{compactBreaksOpen ? 'Hide quantity breaks' : 'Show quantity breaks'}</span>
                                            <i className={'fas fa-chevron-down'} aria-hidden={'true'}></i>
                                        </button>
                                    )}
                                    {shouldShowBreakTableRows && (
                                        <>
                                            <div className={'quantity-break-head'}>
                                                <span>Quantity</span>
                                                <span>Unit Price</span>
                                                <span>Savings</span>
                                            </div>
                                            <div className={'quantity-break-body'}>
                                                {breakpointRows.map((row, idx) => {
                                                    const isActive = idx === activeBreakpointIndex;
                                                    return (
                                                        <button
                                                            type={'button'}
                                                            key={`${selectedUom}-${row.min_qty}`}
                                                            className={`quantity-break-row ${isActive ? 'is-active' : ''}`}
                                                            onClick={() => onBreakpointSelected(row.min_qty)}
                                                        >
                                                            <span className={'quantity-break-qty'}>
                                                                <span className={`break-dot ${isActive ? 'is-active' : ''}`}></span>
                                                                {row.label}
                                                            </span>
                                                            <span>{formatDisplayMoney(
                                                                Number(row.price || 0),
                                                                String(row.currency_code || selectedCurrencyCode),
                                                                String(row.currency_symbol || selectedCurrencySymbol),
                                                            )}</span>
                                                            <span className={'quantity-break-saving'}>
                                                                {row.savingsPercent > 0 ? `Save ${row.savingsPercent}%` : '-'}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {!usesCompactDetailsModal && showStock && (
                        <WarehouseSummary
                            summary={props.warehouseSummary}
                            displayMode={String(props.warehouseSummaryDisplayMode || 'off')}
                            title={'Warehouse availability'}
                            triggerLabel={isCompactMode ? 'Warehouses' : 'Warehouse stock'}
                            triggerClassName={isCompactMode
                                ? 'button weka-secondary-btn button-small weka-add-to-cart__warehouse-trigger'
                                : 'button weka-secondary-btn weka-add-to-cart__warehouse-trigger'}
                            className={'weka-add-to-cart__warehouse-summary'}
                        />
                    )}

                    {!usesCompactDetailsModal && !isCompactMode && effectiveShowPrice && !props.hideCalculatedPrice && (
                        <p className={'calculated-price-inline'}>
                            Total: <strong>{formatDisplayMoney(totalPrice, selectedCurrencyCode, selectedCurrencySymbol)}</strong>
                            {activeUomEnabled && ` (${selectedUomDisplay} x ${formatQuantity(selectedQuantity, decimalAllowed, policyPrecision)})`}
                        </p>
                    )}

                    {shouldShowCompactPriceSummary && !shouldRenderQuantityActions && (
                        <div className={'compact-price-inline compact-price-inline--standalone'}>
                            {showInlineCompactCalculatedPrice && (
                                <div className={'compact-price-inline__total'}>
                                    <strong>
                                        <span className={'compact-price-inline__amount'}>{compactTotalPrice.amount}</span>
                                        {compactTotalPrice.currencyCode !== '' && (
                                            <span className={'compact-price-inline__currency'}>{compactTotalPrice.currencyCode}</span>
                                        )}
                                        {compactTotalPriceSuffix !== '' && (
                                            <span className={'compact-price-inline__suffix'}> {compactTotalPriceSuffix}</span>
                                        )}
                                    </strong>
                                </div>
                            )}
                            {showInlineCompactItemPrice && (
                                <span className={'compact-price-inline__unit'}>
                                    {showComparePrice && (
                                        <>
                                            <del className={'compact-price-inline__compare'}>
                                                {formatDisplayMoney(activeComparePrice, selectedCurrencyCode, selectedCurrencySymbol, false)}
                                            </del>{' '}
                                        </>
                                    )}
                                    <span className={'compact-price-inline__unit-value'}>
                                        {formatDisplayMoney(unitPrice, selectedCurrencyCode, selectedCurrencySymbol)}
                                        {activeUomEnabled ? ` / ${selectedUomDisplay}` : ''}
                                    </span>
                                </span>
                            )}
                        </div>
                    )}

                    {shouldRenderQuantityActions && (
                        <div className={`quantity-actions ${isCompactMode ? 'quantity-actions--compact' : ''}`}>
                            {isCompactMode ? (
                                <>
                                    {(shouldRenderQuantityControls || showInlineCompactCalculatedPrice) && (
                                        <div className={'quantity-actions__compact-top-row'}>
                                            {shouldRenderQuantityControls && (
                                                <div className={'quantity-wrap'}>
                                                    <button
                                                        type={'button'}
                                                        className={'minus-button'}
                                                        onClick={minusButtonClicked}
                                                        aria-label={'Decrease quantity'}
                                                        disabled={!canDecreaseQty}
                                                    >
                                                        <i className="fas fa-minus"></i>
                                                    </button>
                                                    <input
                                                        type={'number'}
                                                        value={quantityInputValue}
                                                        inputMode={decimalAllowed ? 'decimal' : 'numeric'}
                                                        pattern={decimalAllowed ? '[0-9]*[.]?[0-9]*' : '[0-9]*'}
                                                        onChange={onQuantityChange}
                                                        min={allowZeroSelection ? 0 : policyMinNonZeroQty}
                                                        step={quantityInputStep}
                                                        max={policyMaxQty !== null ? policyMaxQty : undefined}
                                                        className={'product-quantity'}
                                                        aria-label={'Quantity'}
                                                        disabled={!canAdjustQuantity || serialQuantityLocked}
                                                    />
                                                    <button
                                                        type={'button'}
                                                        className={'plus-button'}
                                                        onClick={plusButtonClicked}
                                                        aria-label={'Increase quantity'}
                                                        disabled={!canIncreaseQty}
                                                    >
                                                        <i className="fas fa-plus"></i>
                                                    </button>
                                                </div>
                                            )}
                                            {showInlineCompactCalculatedPrice && (
                                                <div className={'compact-price-inline__total compact-price-inline__total--inline'}>
                                                    <strong>
                                                        <span className={'compact-price-inline__amount'}>{compactTotalPrice.amount}</span>
                                                        {compactTotalPrice.currencyCode !== '' && (
                                                            <span className={'compact-price-inline__currency'}>{compactTotalPrice.currencyCode}</span>
                                                        )}
                                                        {compactTotalPriceSuffix !== '' && (
                                                            <span className={'compact-price-inline__suffix'}> {compactTotalPriceSuffix}</span>
                                                        )}
                                                    </strong>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {showInlineCompactItemPrice && (
                                        <div className={'compact-price-inline compact-price-inline--detail'}>
                                            <span className={'compact-price-inline__unit'}>
                                                {showComparePrice && (
                                                    <>
                                                        <del className={'compact-price-inline__compare'}>
                                                            {formatDisplayMoney(activeComparePrice, selectedCurrencyCode, selectedCurrencySymbol, false)}
                                                        </del>{' '}
                                                    </>
                                                )}
                                                <span className={'compact-price-inline__unit-value'}>
                                                    {formatDisplayMoney(unitPrice, selectedCurrencyCode, selectedCurrencySymbol)}
                                                    {activeUomEnabled ? ` / ${selectedUomDisplay}` : ''}
                                                </span>
                                            </span>
                                        </div>
                                    )}
                                </>
                            ) : shouldRenderQuantityControls && (
                                <div className={'quantity-wrap'}>
                                    <button
                                        type={'button'}
                                        className={'minus-button'}
                                        onClick={minusButtonClicked}
                                        aria-label={'Decrease quantity'}
                                        disabled={!canDecreaseQty}
                                    >
                                        <i className="fas fa-minus"></i>
                                    </button>
                                    <input
                                        type={'number'}
                                        value={quantityInputValue}
                                        inputMode={decimalAllowed ? 'decimal' : 'numeric'}
                                        pattern={decimalAllowed ? '[0-9]*[.]?[0-9]*' : '[0-9]*'}
                                        onChange={onQuantityChange}
                                        min={allowZeroSelection ? 0 : policyMinNonZeroQty}
                                        step={quantityInputStep}
                                        max={policyMaxQty !== null ? policyMaxQty : undefined}
                                        className={'product-quantity'}
                                        aria-label={'Quantity'}
                                        disabled={!canAdjustQuantity || serialQuantityLocked}
                                    />
                                    <button
                                        type={'button'}
                                        className={'plus-button'}
                                        onClick={plusButtonClicked}
                                        aria-label={'Increase quantity'}
                                        disabled={!canIncreaseQty}
                                    >
                                        <i className="fas fa-plus"></i>
                                    </button>
                                </div>
                            )}

                            {showEnquiryButton ? (
                                <button
                                    type={'button'}
                                    className={`button weka-add-to-cart-button weka-enquire-now-btn ${isCompactMode ? 'button-small weka-add-to-cart-button--compact loop' : 'button-large single'}`}
                                    data-product-id={props.product.id}
                                    data-product-name={String(props.product.name || '')}
                                >
                                    <i className="fas fa-envelope" aria-hidden="true"></i>
                                    <span>{enquiryButtonText}</span>
                                </button>
                            ) : showBackInStockButton ? (
                                <button
                                    type={'button'}
                                    aria-disabled={isBackInStockSubmitting}
                                    disabled={isBackInStockSubmitting}
                                    onClick={async () => {
                                        if (isBackInStockSubmitting) {
                                            return;
                                        }

                                        if (isCurrentBackInStockSubscribed) {
                                            await removeBackInStockSubscription();
                                            return;
                                        }

                                        await subscribeToBackInStock();
                                    }}
                                    className={`button weka-add-to-cart-button ${isCompactMode ? 'button-small weka-add-to-cart-button--compact' : 'button-large'} ${isBackInStockSubmitting ? 'disabled' : ''}`}
                                >
                                    {isBackInStockSubmitting
                                        ? 'Saving...'
                                        : (isCurrentBackInStockSubscribed ? backInStockRemoveButtonLabel : backInStockButtonLabel)}
                                </button>
                            ) : canShowAddToCartButton ? (
                                <button
                                    type={'button'}
                                    aria-disabled={!canTriggerAddToCart}
                                    disabled={!canTriggerAddToCart}
                                    onClick={async () => {
                                        if (!canTriggerAddToCart) {
                                            return;
                                        }

                                        await addToCartAjax();
                                    }}
                                    className={`button weka-add-to-cart-button ${isCompactMode ? 'button-small weka-add-to-cart-button--compact' : 'button-large'} ${canTriggerAddToCart ? '' : 'disabled'}`}
                                >
                                    {addToCartButtonLabel}
                                </button>
                            ) : null}
                        </div>
                    )}

                    {!usesCompactDetailsModal && canShowCompactElement('messages') && (activePricingUnavailableMessage !== '' || backInStockNotice !== '' || quantityNotice !== '' || quantityHint !== '') && (
                        <p className={'unit-price-helper'}>
                            {activePricingUnavailableMessage !== '' && <span className={'text-danger'}>{activePricingUnavailableMessage}</span>}
                            {activePricingUnavailableMessage !== '' && (backInStockNotice !== '' || quantityNotice !== '' || quantityHint !== '') && ' '}
                            {backInStockNotice !== '' && <span>{backInStockNotice}</span>}
                            {backInStockNotice !== '' && (quantityNotice !== '' || quantityHint !== '') && ' '}
                            {quantityNotice !== '' && <span>{quantityNotice}</span>}
                            {quantityNotice !== '' && quantityHint !== '' && ' '}
                            {quantityHint !== '' && <span>{quantityHint}</span>}
                        </p>
                    )}

                    {!usesCompactDetailsModal && (canShowDraftOrderButton || canShowQuoteButton) && (
                        <div className={`weka-cart-buttons ${isCompactMode ? 'weka-cart-buttons--compact' : ''}`}>
                            {canShowDraftOrderButton && (
                                <button
                                    type="button"
                                    className={`button weka-secondary-btn ${isCompactMode ? 'button-small weka-secondary-action-button weka-compact-secondary-button weka-compact-secondary-button--draft' : 'button-large weka-secondary-action-button weka-secondary-action-button--draft'}`}
                                    style={{width: '100%'}}
                                    disabled={!canBuildSavedItem}
                                    aria-label={'Add to Draft Order'}
                                    onClick={() => {
                                        const payload = buildDraftItemPayload();
                                        if (!payload) {
                                            return;
                                        }

                                        setDraftItemPayload(payload);
                                        setIsDraftSelectorOpen(true);
                                    }}
                                >
                                    {isCompactMode ? 'Save Draft' : 'Add to Draft Order'}
                                </button>
                            )}
                            {canShowQuoteButton && (
                                <button
                                    type="button"
                                    className={`button weka-secondary-btn ${isCompactMode ? 'button-small weka-secondary-action-button weka-compact-secondary-button weka-compact-secondary-button--quote' : 'button-large weka-secondary-action-button weka-secondary-action-button--quote'}`}
                                    style={{width: '100%'}}
                                    disabled={!canBuildSavedItem}
                                    aria-label={'Add to Quote'}
                                    onClick={() => {
                                        const payload = buildQuoteItemPayload();
                                        if (!payload) {
                                            return;
                                        }

                                        setQuoteItemPayload(payload);
                                        setIsQuoteSelectorOpen(true);
                                    }}
                                >
                                    {isCompactMode ? 'Save Quote' : 'Add to Quote'}
                                </button>
                            )}
                        </div>
                    )}

                    {!usesCompactDetailsModal && !isCompactMode && effectiveShowPrice && (!props.hideCalculatedPrice || showComparePrice) && (
                        <p className={'unit-price-helper'}>
                            Unit Price:{' '}
                            {showComparePrice && (
                                <>
                                    <del>{formatDisplayMoney(activeComparePrice, selectedCurrencyCode, selectedCurrencySymbol, false)}</del>{' '}
                                </>
                            )}
                            {formatDisplayMoney(unitPrice, selectedCurrencyCode, selectedCurrencySymbol)} {activeUomEnabled ? `/ ${selectedUomDisplay}` : ''}
                        </p>
                    )}
                    {usesCompactDetailsModal && isDetailsModalOpen && (
                        <div
                            className="weka-compact-details-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={`weka-compact-details-modal-title-${props.product.id}`}
                            onClick={closeDetailsModal}
                        >
                            <div
                                className="weka-compact-details-modal__dialog"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="weka-compact-details-modal__header">
                                    <h2
                                        id={`weka-compact-details-modal-title-${props.product.id}`}
                                        className="weka-compact-details-modal__title"
                                    >
                                        {detailsModalProduct?.name || String(props.product.name || 'Product details')}
                                    </h2>
                                    <button
                                        ref={detailsCloseButtonRef}
                                        type="button"
                                        className="weka-compact-details-modal__close"
                                        aria-label="Close product details"
                                        onClick={closeDetailsModal}
                                    >
                                        <span aria-hidden="true">x</span>
                                    </button>
                                </div>
                                <div className="weka-compact-details-modal__body">
                                    {isDetailsModalLoading && (
                                        <div className="weka-compact-details-modal__status">
                                            Loading product details...
                                        </div>
                                    )}
                                    {!isDetailsModalLoading && detailsModalError !== '' && (
                                        <div className="alert alert-danger mb-0" role="alert">
                                            {detailsModalError}
                                        </div>
                                    )}
                                    {!isDetailsModalLoading && detailsModalError === '' && !detailsModalProduct && (
                                        <div className="alert alert-warning mb-0" role="alert">
                                            Product not found.
                                        </div>
                                    )}
                                    {!isDetailsModalLoading && detailsModalProduct && (
                                        <ProductDetailSurface
                                            product={detailsModalProduct}
                                            settings={detailsModalResponse.settings}
                                            addToCart={(
                                                <WekaAddToCart
                                                    {...buildProductDetailAddToCartProps(detailsModalProduct, detailsModalResponse.settings)}
                                                />
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
            {!usesCompactDetailsModal && canShowCompactElement('favouriteButton') && !isTruthy(props.hideFavouriteButton) && (
                <FavouriteButton
                    productId={props.product.id}
                    initialIsFavourite={props.isFavourite}
                    displayMode={isCompactMode ? 'compact' : 'full'}
                    className={isCompactMode ? 'button weka-secondary-btn button-small weka-compact-favourite-button' : 'button weka-secondary-btn weka-full-favourite-button'}
                    canToggle={normalizeOptionalBoolean(props.canToggleFavourites)}
                    ajaxUrl={String(props.favouritesAjaxUrl || '').trim() || undefined}
                    nonce={String(props.favouritesNonce || '').trim() || undefined}
                    groupsEnabled={normalizeOptionalBoolean(props.favouritesGroupsEnabled)}
                />
            )}
            <DraftOrderSelectorModal
                isOpen={isDraftSelectorOpen}
                onClose={() => setIsDraftSelectorOpen(false)}
                itemPayload={draftItemPayload}
                itemName={String(props.product.name || '').trim()}
                canCreateDraft={props.canCreateDraftOrders === true || props.canCreateDraftOrders === 'true'}
                canAddToExisting={props.canEditDraftItems === true || props.canEditDraftItems === 'true'}
                onSuccess={() => {
                    resetQuantityAfterSuccess();
                    setDraftItemPayload(null);
                    setQuantityNotice('');
                }}
            />
            <QuoteSelectorModal
                isOpen={isQuoteSelectorOpen}
                onClose={() => setIsQuoteSelectorOpen(false)}
                itemPayload={quoteItemPayload}
                itemName={String(props.product.name || '').trim()}
                canCreateQuote={props.canCreateQuotes === true || props.canCreateQuotes === 'true'}
                canAddToExisting={props.canEditQuotes === true || props.canEditQuotes === 'true'}
                onSuccess={() => {
                    resetQuantityAfterSuccess();
                    setQuoteItemPayload(null);
                    setQuantityNotice('');
                }}
            />
        </div>
    );
};

export default WekaAddToCart;
